<template>
  <div class="wizard-page">
    <!-- Header -->
    <header class="wizard-header">
      <div class="header-content">
        <div class="header-left">
          <transition :name="skipLogoAnimation ? '' : 'back-btn-fade'">
            <button v-if="currentStep > 1" class="header-back-btn" @click="prevStep">
              <span>←</span>
            </button>
          </transition>
          <img
            src="@/assets/images/NewSelf_Logo.webp"
            alt="NewSelf"
            class="logo"
            :class="{ 'logo-shifted': currentStep > 1, 'no-transition': skipLogoAnimation }"
          />
        </div>
        <div class="header-right">
          <a :href="supportPhoneTel" class="phone-info">
            <svg class="phone-icon-mobile" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            <span class="phone-text">
              <span class="phone-label">Questions? Call us</span>
              <span v-if="supportPhone" class="phone-number">{{ supportPhone }}</span>
            </span>
          </a>
          <div class="secure-badge">
            <svg class="lock-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
            </svg>
            <div class="secure-text-group">
              <p class="secure-text">SECURE</p>
              <p class="payment-text">PAYMENT</p>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- 8-Step Wizard Stepper (Steps 1-8) -->
    <div v-if="currentStep < 9" class="progress-bar">
      <div class="progress-content">
        <div v-for="step in 8" :key="'step-' + step" class="step-wrapper">
          <!-- Step circle -->
          <div
            class="step-circle"
            :class="{
              'completed': step < currentStep,
              'active': step === currentStep
            }"
          >
            <!-- Checkmark for completed -->
            <svg v-if="step < currentStep" class="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
            </svg>
            <!-- Dot for active -->
            <div v-else-if="step === currentStep" class="step-dot"></div>
            <!-- Empty for future steps -->
          </div>
          <!-- Connector line (not after last step) -->
          <div v-if="step < 8" class="step-line" :class="{ 'completed': step < currentStep }"></div>
        </div>
      </div>
    </div>

    <!-- 2-Step Checkout Stepper (Step 9) -->
    <div v-if="currentStep === 9" class="progress-bar checkout-stepper">
      <div class="progress-content">
        <div class="checkout-step active">
          <div class="checkout-step-circle active">
            <div class="step-dot"></div>
          </div>
          <span class="checkout-step-label">Choose Plan</span>
        </div>
        <div class="checkout-step-line"></div>
        <div class="checkout-step">
          <div class="checkout-step-circle">
            <div class="step-dot-outline"></div>
          </div>
          <span class="checkout-step-label">Checkout</span>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <main class="wizard-main" :class="{ 'wizard-main-wide': currentStep === 7 || currentStep === 9 }">
      <!-- Step 1: State, Sex, DOB -->
      <div v-if="currentStep === 1" :key="'step-1'" class="wizard-content">
        <div class="step-header">
          <h1 class="step-title">Get Pre-Qualified in Minutes!</h1>
          <p class="step-subtitle">Get your medication in as little as 72 Hours.</p>
        </div>

        <form class="wizard-form" @submit.prevent="handleStep1Submit">
          <div class="form-group">
            <label class="form-label">State*</label>
            <div class="state-autocomplete" :class="{ 'field-error': fieldErrors.state }">
              <input
                ref="stateInput"
                type="text"
                class="state-input"
                :value="stateSearchText"
                @input="onStateInput($event.target.value)"
                @focus="onStateFocus"
                @blur="onStateBlur"
                @keydown.down.prevent="onStateKeyDown"
                @keydown.up.prevent="onStateKeyUp"
                @keydown.enter.prevent="onStateEnter"
                @keydown.escape="onStateEscape"
                placeholder="Type or select your state..."
                autocomplete="off"
              />
              <div v-if="showStateSuggestions && stateSearchText.trim() && filteredStates.length > 0" class="state-suggestions">
                <button
                  v-for="(item, index) in filteredStates"
                  :key="item.abbr"
                  type="button"
                  class="state-suggestion-item"
                  :class="{ 'is-highlighted': stateHighlightIndex === index, 'is-selected': people.state === item.abbr }"
                  @mousedown.prevent="selectState(item.abbr)"
                >
                  {{ item.name }}
                </button>
              </div>
            </div>
          </div>

          <div class="form-group" :class="{ 'dropdown-open': genderDropdownOpen }">
            <label class="form-label">Sex assigned at birth*</label>
            <div class="custom-dropdown" :class="{ 'is-open': genderDropdownOpen, 'field-error': fieldErrors.gender }">
              <button type="button" class="dropdown-trigger" @click="genderDropdownOpen = !genderDropdownOpen">
                <span>{{ people.gender || 'Select an option...' }}</span>
                <svg class="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              <div v-if="genderDropdownOpen" class="dropdown-overlay" @click="genderDropdownOpen = false"></div>
              <div v-if="genderDropdownOpen" class="dropdown-menu">
                <button type="button" class="dropdown-option" :class="{ 'is-selected': people.gender === 'Male' }" @click="selectGender('Male')">
                  <span>Male</span>
                </button>
                <button type="button" class="dropdown-option" :class="{ 'is-selected': people.gender === 'Female' }" @click="selectGender('Female')">
                  <span>Female</span>
                </button>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Date of birth*</label>
            <div class="dob-row">
              <input
                type="text"
                v-model="people.dob.mm"
                placeholder="MM"
                maxlength="2"
                inputmode="numeric"
                pattern="[0-9]*"
                required
                :class="{ 'field-error': fieldErrors.dob_mm }"
                @input="filterDobInput('mm'); autoAdvanceDob('mm')"
                ref="dobMm"
              />
              <input
                type="text"
                v-model="people.dob.dd"
                placeholder="DD"
                maxlength="2"
                inputmode="numeric"
                pattern="[0-9]*"
                required
                :class="{ 'field-error': fieldErrors.dob_dd }"
                @input="filterDobInput('dd'); autoAdvanceDob('dd')"
                ref="dobDd"
              />
              <input
                type="text"
                v-model="people.dob.yyyy"
                placeholder="YYYY"
                maxlength="4"
                inputmode="numeric"
                pattern="[0-9]*"
                required
                :class="{ 'field-error': fieldErrors.dob_yyyy }"
                @input="filterDobInput('yyyy')"
                ref="dobYyyy"
              />
            </div>
          </div>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

          <div class="form-actions">
            <button type="submit" class="continue-btn">
              Continue <span class="btn-arrow">→</span>
            </button>
          </div>

        </form>
      </div>

      <!-- Step 2: Height, Weight -->
      <div v-if="currentStep === 2" :key="'step-2'" class="wizard-content">
        <div class="step-header">
          <h1 class="step-title">Tell us about yourself</h1>
          <p class="step-subtitle">Helps confirm you qualify for program.</p>
        </div>

        <form class="wizard-form" @submit.prevent="handleStep2Submit">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Height* (Feet)</label>
              <input
                id="height-feet-v4"
                type="text"
                v-model="people.height.foot"
                placeholder="eg. 5"
                maxlength="1"
                inputmode="numeric"
                pattern="[0-9]*"
                required
                :class="{ 'field-error': fieldErrors.height_foot }"
                @input="filterNumericInput('height.foot'); clampFeet(); autoAdvanceHeight('feet')"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Height* (Inches)</label>
              <input
                id="height-inches-v4"
                type="text"
                v-model="people.height.inches"
                placeholder="eg. 3"
                maxlength="2"
                inputmode="numeric"
                pattern="[0-9]*"
                required
                :class="{ 'field-error': fieldErrors.height_inches }"
                @input="filterNumericInput('height.inches'); clampInches(); autoAdvanceHeight('inches')"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Weight* (Pounds)</label>
            <input
              id="weight-pounds-v4"
              type="text"
              v-model="people.weight"
              placeholder="eg. 200"
              maxlength="3"
              inputmode="numeric"
              pattern="[0-9]*"
              required
              :class="{ 'field-error': fieldErrors.weight }"
              @input="filterNumericInput('weight'); clampWeight()"
            />
          </div>

          <div v-if="bmiCalculated" class="bmi-display">
            <svg class="bmi-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
            </svg>
            <span>Your BMI Score is <strong>{{ bmiCalculated }}</strong></span>
          </div>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

          <div class="form-actions">
            <button type="submit" class="continue-btn">
              Continue <span class="btn-arrow">→</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Step 3: Name, Email, Phone -->
      <div v-if="currentStep === 3" :key="'step-3'" class="wizard-content">
        <div class="step-header">
          <h1 class="step-title">Enter your details to save your progress</h1>
          <p class="step-subtitle">A doctor will prescribe your medication in less than 24 hours.</p>
        </div>

        <form class="wizard-form" @submit.prevent="handleStep3Submit">
          <div class="form-group">
            <label class="form-label">First name*</label>
            <input
              type="text"
              v-model="people.first_name"
              placeholder="Your first name"
              required
              :class="{ 'field-error': fieldErrors.first_name }"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Last name*</label>
            <input
              type="text"
              v-model="people.last_name"
              placeholder="Your last name"
              required
              :class="{ 'field-error': fieldErrors.last_name }"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Email*</label>
            <input
              type="email"
              v-model="people.email"
              placeholder="Email address"
              required
              :class="{ 'field-error': fieldErrors.email || emailCheckError, 'email-error-highlight': emailCheckError }"
              @blur="checkEmailOnBlur"
            />
            <p class="paragraph-218">By entering your email, we'll save your progress and send you updates about your care. You can unsubscribe anytime. View our <a href="https://newself.com/terms-of-use" target="_blank">Terms & Conditions</a>.</p>
            <div v-if="emailCheckError" class="email-check-error">{{ emailCheckError }}</div>
          </div>

          <div class="form-group">
            <label class="form-label">Phone Number*</label>
            <input
              type="text"
              v-model="phoneDisplay"
              placeholder="(XXX) XXX-XXXX"
              maxlength="16"
              inputmode="numeric"
              autocomplete="off"
              required
              :class="{ 'field-error': fieldErrors.phone }"
              @input="handlePhoneInput"
            />
            <div class="sms-consent-row" :class="{ 'sms-consent-shake': smsConsentShake, 'sms-consent-row-error': smsConsentError }">
              <input type="checkbox" id="checkbox-sms" name="checkbox-sms" class="sms-consent-checkbox" v-model="people.agree_to_phone" @change="smsConsentError = ''" />
              <label for="checkbox-sms" class="sms-phone-disclaimer">By checking the box, you agree that NewSelf can contact you at the telephone number provided using an autodialer, AI technology, artificial voice and prerecorded calls or text/SMS messages with information and offers. Msg. and data rates apply. You also agree to our <a href="https://www.newself.com/privacy-policy" target="_blank" class="sms-phone-disclaimer-link" @click.stop>Privacy Policy</a> and <a href="https://newself.com/terms-of-use" target="_blank" class="sms-phone-disclaimer-link" @click.stop>Terms</a>.</label>
            </div>
          </div>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

          <div class="form-actions sms-actions">
            <div v-if="smsConsentError" class="sms-consent-error">{{ smsConsentError }}</div>
            <button type="submit" class="continue-btn" :disabled="isSubmitting || isCheckingEmail">
              <span v-if="isSubmitting || isCheckingEmail" class="spinner"></span>
              <span v-else>Find Your Treatment</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Step 4: Taking GLP-1? -->
      <div v-if="currentStep === 4" :key="'step-4'" class="wizard-content">
        <div class="step-header">
          <h1 class="step-title">Are you on GLP-1 medication?</h1>
          <p class="step-subtitle">Your healthcare practitioner will review your treatment before finalizing.</p>
        </div>

        <div class="option-cards">
          <div
            class="option-card"
            :class="{ 'selected': isTakingGLP1 === true }"
            @click="handleGLP1Selection(true)"
          >
            <div class="option-radio" :class="{ 'checked': isTakingGLP1 === true }">
              <div v-if="isTakingGLP1 === true" class="radio-dot"></div>
            </div>
            <div class="option-text">
              <strong>Yes</strong>, I'm currently taking GLP-1 medication
            </div>
          </div>

          <div
            class="option-card"
            :class="{ 'selected': isTakingGLP1 === false }"
            @click="handleGLP1Selection(false)"
          >
            <div class="option-radio" :class="{ 'checked': isTakingGLP1 === false }">
              <div v-if="isTakingGLP1 === false" class="radio-dot"></div>
            </div>
            <div class="option-text">
              <strong>No</strong>, but I would like to start
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      </div>

      <!-- Step 5: Which medication? (only if taking GLP-1) -->
      <div v-if="currentStep === 5" :key="'step-5'" class="wizard-content">
        <div class="step-header">
          <h1 class="step-title">Which medication are you taking?</h1>
          <p class="step-subtitle">Select the weight loss medication you're currently using.</p>
        </div>

        <div class="option-cards">
          <div
            class="option-card"
            :class="{ 'selected': currentMedicationType === 'semaglutide' }"
            @click="handleCurrentMedicationSelection('semaglutide')"
          >
            <div class="option-radio" :class="{ 'checked': currentMedicationType === 'semaglutide' }">
              <div v-if="currentMedicationType === 'semaglutide'" class="radio-dot"></div>
            </div>
            <div class="option-text">Semaglutide Compound</div>
          </div>

          <div
            class="option-card"
            :class="{ 'selected': currentMedicationType === 'tirzepatide' }"
            @click="handleCurrentMedicationSelection('tirzepatide')"
          >
            <div class="option-radio" :class="{ 'checked': currentMedicationType === 'tirzepatide' }">
              <div v-if="currentMedicationType === 'tirzepatide'" class="radio-dot"></div>
            </div>
            <div class="option-text">Tirzepatide Compound</div>
          </div>

          <div
            class="option-card"
            :class="{ 'selected': currentMedicationType === 'mounjaro' }"
            @click="handleCurrentMedicationSelection('mounjaro')"
          >
            <div class="option-radio" :class="{ 'checked': currentMedicationType === 'mounjaro' }">
              <div v-if="currentMedicationType === 'mounjaro'" class="radio-dot"></div>
            </div>
            <div class="option-text">Mounjaro</div>
          </div>

          <div
            class="option-card"
            :class="{ 'selected': currentMedicationType === 'ozempic' }"
            @click="handleCurrentMedicationSelection('ozempic')"
          >
            <div class="option-radio" :class="{ 'checked': currentMedicationType === 'ozempic' }">
              <div v-if="currentMedicationType === 'ozempic'" class="radio-dot"></div>
            </div>
            <div class="option-text">Ozempic</div>
          </div>
        </div>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      </div>

      <!-- Step 6: Choose dosage -->
      <div v-if="currentStep === 6" :key="'step-6'" class="wizard-content">
        <div class="step-header">
          <h1 class="step-title">What dose are you currently taking?</h1>
          <p class="step-subtitle">Select your current dosage level.</p>
        </div>

        <div class="option-cards">
          <!-- Semaglutide dosages (for Semaglutide and Ozempic) -->
          <template v-if="currentMedicationType === 'semaglutide' || currentMedicationType === 'ozempic'">
            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '0.25 mg' }"
              @click="selectDosage('semaglutide', '0.25 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '0.25 mg' }">
                <div v-if="selectedDosage === '0.25 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Semaglutide <strong>0.25 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '0.5 mg' }"
              @click="selectDosage('semaglutide', '0.5 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '0.5 mg' }">
                <div v-if="selectedDosage === '0.5 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Semaglutide <strong>0.5 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '1.0 mg' }"
              @click="selectDosage('semaglutide', '1.0 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '1.0 mg' }">
                <div v-if="selectedDosage === '1.0 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Semaglutide <strong>1.0 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '2.0 mg' }"
              @click="selectDosage('semaglutide', '2.0 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '2.0 mg' }">
                <div v-if="selectedDosage === '2.0 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Semaglutide <strong>2.0 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '2.5 mg' }"
              @click="selectDosage('semaglutide', '2.5 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '2.5 mg' }">
                <div v-if="selectedDosage === '2.5 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Semaglutide <strong>2.5 mg</strong></div>
            </div>
          </template>

          <!-- Tirzepatide dosages (for Tirzepatide and Mounjaro) -->
          <template v-if="currentMedicationType === 'tirzepatide' || currentMedicationType === 'mounjaro'">
            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '2.5 mg' }"
              @click="selectDosage('tirzepatide', '2.5 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '2.5 mg' }">
                <div v-if="selectedDosage === '2.5 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Tirzepatide <strong>2.5 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '5.0 mg' }"
              @click="selectDosage('tirzepatide', '5.0 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '5.0 mg' }">
                <div v-if="selectedDosage === '5.0 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Tirzepatide <strong>5.0 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '7.5 mg' }"
              @click="selectDosage('tirzepatide', '7.5 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '7.5 mg' }">
                <div v-if="selectedDosage === '7.5 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Tirzepatide <strong>7.5 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '10.0 mg' }"
              @click="selectDosage('tirzepatide', '10.0 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '10.0 mg' }">
                <div v-if="selectedDosage === '10.0 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Tirzepatide <strong>10.0 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '12.5 mg' }"
              @click="selectDosage('tirzepatide', '12.5 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '12.5 mg' }">
                <div v-if="selectedDosage === '12.5 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Tirzepatide <strong>12.5 mg</strong></div>
            </div>

            <div
              class="option-card"
              :class="{ 'selected': selectedDosage === '15.0 mg' }"
              @click="selectDosage('tirzepatide', '15.0 mg')"
            >
              <div class="option-radio" :class="{ 'checked': selectedDosage === '15.0 mg' }">
                <div v-if="selectedDosage === '15.0 mg'" class="radio-dot"></div>
              </div>
              <div class="option-text">Tirzepatide <strong>15.0 mg</strong></div>
            </div>
          </template>
        </div>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      </div>

      <!-- Step 7: Choose Treatment -->
      <div v-if="currentStep === 7" :key="'step-7'" class="wizard-content">
        <div class="step-header">
          <h1 class="step-title">Choose your Treatment</h1>
          <p class="step-subtitle">Your healthcare practitioner will review your treatment before finalizing.</p>
        </div>

        <div class="treatment-cards">
          <!-- Tirzepatide+ Card -->
          <div class="treatment-card" @click="selectMedication('tirzepatide')">
            <div class="treatment-image tirz">
              <img src="@/assets/images/TirzTilt.webp" alt="Tirzepatide+" />
              <div class="price-tag">
                <span class="price-from">From</span>
                <span class="price-amount price-amt" :class="{ 'price-loading': !pricesReady }">${{ lowestTirzPrice.dollars }}<sup>{{ lowestTirzPrice.cents }}</sup></span>
              </div>
            </div>
            <div class="treatment-info">
              <div class="treatment-header">
                <div class="treatment-name-group">
                  <h3 class="treatment-name">Tirzepatide<span class="plus">+</span></h3>
                  <span class="treatment-sub">Compounded w/ Glycine or B6</span>
                </div>
                <button class="select-btn" :disabled="isLoading">
                  <span v-if="isLoading && selectedMedication === 'tirzepatide'" class="btn-spinner"></span>
                  <span v-else>Select</span>
                </button>
              </div>
              <p class="treatment-desc">The most popular, GLP-1 weight loss program that works on two Glucagon receptors.</p>
            </div>
            <img loading="lazy" src="@/assets/images/klarna-badge.webp" alt="Klarna" class="klarna-badge">
          </div>

          <!-- Semaglutide+ Card -->
          <div class="treatment-card" @click="selectMedication('semaglutide')">
            <div class="treatment-image sema">
              <img src="@/assets/images/SemaTilt_1.webp" alt="Semaglutide+" />
              <div class="price-tag">
                <span class="price-from">From</span>
                <span class="price-amount price-amt" :class="{ 'price-loading': !pricesReady }">${{ lowestSemaPrice.dollars }}<sup>{{ lowestSemaPrice.cents }}</sup></span>
              </div>
            </div>
            <div class="treatment-info">
              <div class="treatment-header">
                <div class="treatment-name-group">
                  <h3 class="treatment-name">Semaglutide<span class="plus">+</span></h3>
                  <span class="treatment-sub">Compounded w/ Glycine or B12</span>
                </div>
                <button class="select-btn" :disabled="isLoading">
                  <span v-if="isLoading && selectedMedication === 'semaglutide'" class="btn-spinner"></span>
                  <span v-else>Select</span>
                </button>
              </div>
              <p class="treatment-desc">The original, GLP-1 weight loss program that works on one Glucagon receptor.</p>
            </div>
            <img loading="lazy" src="@/assets/images/klarna-badge.webp" alt="Klarna" class="klarna-badge">
          </div>

          <!-- Mounjaro Card (Out of Stock) -->
          <div class="treatment-card brand-card" @click="showBrandOutOfStock('mounjaro')">
            <div class="treatment-image mounjaro">
              <img src="@/assets/images/tilted-mounjaro.webp" alt="Mounjaro" />
              <div class="price-tag brand">
                <span class="price-amount">$1,899<sup>97</sup></span>
              </div>
            </div>
            <div class="treatment-info">
              <div class="treatment-header">
                <div class="treatment-name-group">
                  <h3 class="treatment-name">Mounjaro<span class="trademark">™</span></h3>
                  <span class="treatment-sub">Developed by Eli Lilly™</span>
                </div>
                <button class="select-btn">Select</button>
              </div>
              <p class="treatment-desc">The most popular dual-action GLP-1 weight loss program with enhanced metabolic support.</p>
            </div>
            <img loading="lazy" src="@/assets/images/klarna-badge.webp" alt="Klarna" class="klarna-badge">
          </div>

          <!-- Ozempic Card (Out of Stock) -->
          <div class="treatment-card brand-card" @click="showBrandOutOfStock('ozempic')">
            <div class="treatment-image ozempic">
              <img src="@/assets/images/tilted-ozempic.webp" alt="Ozempic" />
              <div class="price-tag brand">
                <span class="price-amount">$1,495<sup>97</sup></span>
              </div>
            </div>
            <div class="treatment-info">
              <div class="treatment-header">
                <div class="treatment-name-group">
                  <h3 class="treatment-name">Ozempic<span class="trademark">™</span></h3>
                  <span class="treatment-sub">Developed by Novo Nordisk™</span>
                </div>
                <button class="select-btn">Select</button>
              </div>
              <p class="treatment-desc">A leading GLP-1 weight loss program that targets appetite regulation.</p>
            </div>
            <img loading="lazy" src="@/assets/images/klarna-badge.webp" alt="Klarna" class="klarna-badge">
          </div>
        </div>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      </div>

      <!-- Step 8: Inclusions -->
      <div v-if="currentStep === 8" :key="'step-8'" class="wizard-content inclusions-step">
        <div class="step-header">
          <h1 class="step-title">More than Just Medication.</h1>
        </div>

        <div class="inclusions-list">
          <!-- Selected Medication -->
          <div class="inclusion-item">
            <div class="inclusion-left">
              <div class="inclusion-icon">
                <img :src="selectedMedication === 'semaglutide' ? require('@/assets/images/SemaTilt_1.webp') : require('@/assets/images/TirzTilt.webp')" alt="Medication" />
              </div>
              <div class="inclusion-text">
                <h4>{{ selectedMedication === 'semaglutide' ? 'Semaglutide' : 'Tirzepatide' }}</h4>
                <p>Personalized Compounded GLP Medication - Subcutaneous</p>
              </div>
            </div>
            <div class="inclusion-right">
              <span class="inclusion-price">As low as <span class="price-amt" :class="{ 'price-loading': !pricesReady }"><strong>${{ selectedMedication === 'semaglutide' ? lowestSemaPrice.dollars : lowestTirzPrice.dollars }}</strong><sup>{{ selectedMedication === 'semaglutide' ? lowestSemaPrice.cents : lowestTirzPrice.cents }}</sup></span>/mo</span>
            </div>
          </div>

          <!-- Doctor Review -->
          <div class="inclusion-item">
            <div class="inclusion-left">
              <div class="inclusion-icon">
                <img src="@/assets/images/icon_stetoscope.webp" alt="Doctor" />
              </div>
              <div class="inclusion-text">
                <h4>Doctor Review</h4>
                <p>A doctor will review your intake, and prescribe (if appropriate) today!</p>
              </div>
            </div>
            <div class="inclusion-right">
              <span class="included-badge">Included</span>
            </div>
          </div>

          <!-- Discounted Therapies -->
          <div class="inclusion-item">
            <div class="inclusion-left">
              <div class="inclusion-icon">
                <img src="@/assets/images/icon_discount.webp" alt="Discount" />
              </div>
              <div class="inclusion-text">
                <h4>Discounted Therapies</h4>
                <p>Accelerate your results and reduce side effects with additional treatment plans.</p>
              </div>
            </div>
            <div class="inclusion-right">
              <span class="included-badge">Included</span>
            </div>
          </div>

          <!-- Metabolic Weight Plan -->
          <div class="inclusion-item">
            <div class="inclusion-left">
              <div class="inclusion-icon inclusion-icon-bottom">
                <img src="@/assets/images/WeightSteps_Plan2.webp" alt="Plan" />
              </div>
              <div class="inclusion-text">
                <h4>Metabolic Weight Plan</h4>
                <p>A Personalized weight loss plan to help you lose weight faster and keep it off.</p>
              </div>
            </div>
            <div class="inclusion-right">
              <span class="included-badge">Included</span>
            </div>
          </div>

          <!-- Overnight Shipping -->
          <div class="inclusion-item">
            <div class="inclusion-left">
              <div class="inclusion-icon">
                <img src="@/assets/images/icon_van.webp" alt="Shipping" />
              </div>
              <div class="inclusion-text">
                <h4>Overnight Cold Shipping</h4>
                <p>We ship all medications <strong>next-day on cold packs</strong> to keep them stable and effective.</p>
              </div>
            </div>
            <div class="inclusion-right">
              <span class="included-badge">Included</span>
            </div>
          </div>

          <!-- Ongoing Support -->
          <div class="inclusion-item">
            <div class="inclusion-left">
              <div class="inclusion-icon inclusion-icon-bottom">
                <img src="@/assets/images/Doctor_newself.webp" alt="Support" />
              </div>
              <div class="inclusion-text">
                <h4>Ongoing Doctor Support</h4>
                <p>Our doctors provide ongoing support and quarterly check-ins to monitor your progress.</p>
              </div>
            </div>
            <div class="inclusion-right">
              <span class="included-badge">Included</span>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button class="continue-btn" @click="advanceStep">
            Continue <span class="btn-arrow">→</span>
          </button>
        </div>

        <!-- Trust Pilot -->
        <div class="trust-row trustpilot">
          <div class="stars">★★★★★</div>
          <div class="trust-info">
            <span class="trust-rating">Excellent 4.6 out of 5</span>
          </div>
          <span class="trust-count">Trusted by thousands of members</span>
        </div>
      </div>

      <!-- Step 9: Choose Plan -->
      <div v-if="currentStep === 9" :key="'step-9'" class="wizard-content choose-plan-step step-9-layout">
        <div class="step-header">
          <h1 class="step-title">Complete your order</h1>
        </div>

        <div class="step-9-grid">
          <!-- Left Column: Plan Selection -->
          <div class="step-9-left">
            <!-- Product Info Card -->
            <div class="product-info-card">
              <div class="product-info-content">
                <div class="product-info-header">
                  <div class="product-name-section">
                    <div class="product-name">{{ selectedMedication === 'semaglutide' ? 'Semaglutide' : 'Tirzepatide' }}<span class="product-plus">+</span></div>
                    <div class="product-compound">COMPOUNDED W/ {{ selectedMedication === 'semaglutide' ? 'GLYCINE OR B12' : 'GLYCINE OR B6' }}</div>
                  </div>
                  <div class="dosage-boxes">
                    <div class="dosage-box">
                      <div class="dosage-month">MONTH 1</div>
                      <div class="dosage-amount">{{ selectedMedication === 'semaglutide' ? '0.25' : '2.5' }} <span class="dosage-unit">MG</span></div>
                      <div class="dosage-freq">Weekly Dose</div>
                    </div>
                    <div class="dosage-box">
                      <div class="dosage-month">MONTH 2</div>
                      <div class="dosage-amount">{{ selectedMedication === 'semaglutide' ? '0.5' : '5' }} <span class="dosage-unit">MG</span></div>
                      <div class="dosage-freq">Weekly Dose</div>
                    </div>
                    <div class="dosage-box">
                      <div class="dosage-month">MONTH 3</div>
                      <div class="dosage-amount">{{ selectedMedication === 'semaglutide' ? '1' : '7.5' }} <span class="dosage-unit">MG</span></div>
                      <div class="dosage-freq">Weekly Dose</div>
                    </div>
                  </div>
                </div>
                <div class="product-info-note">All Dosages are same price. Meet w/ provider to personalize plan.</div>
              </div>
              <div class="product-info-image" :class="selectedMedication">
                <img :src="selectedMedication === 'semaglutide' ? 'https://newselfrx.s3.us-east-1.amazonaws.com/public/assets/product_image/webp/semaglutide/1.webp' : 'https://newselfrx.s3.us-east-1.amazonaws.com/public/assets/product_image/webp/tirzepatide/1.webp'" :alt="selectedMedication" />
              </div>
            </div>

            <!-- Plan Rows -->
            <div class="plan-rows">
              <!-- Monthly Plan -->
              <div
                class="plan-row"
                :class="{ selected: paymentInfo.payment_terms === 1 }"
                @click="changePaymentTerms(1)"
              >
                <div class="plan-row-left">
                  <div class="glp1-radio-circle" :class="{ checked: paymentInfo.payment_terms === 1 }"></div>
                  <div class="plan-row-info">
                    <div class="plan-row-price-inline"><span class="price-amt" :class="{ 'price-loading': !pricesReady }"><strong>${{ productPricing.monthly.regular.toFixed(2).split('.')[0] }}</strong><sup>{{ productPricing.monthly.regular.toFixed(2).split('.')[1] }}</sup></span>/mo</div>
                    <div class="plan-disclaimer">*3 month supply paid monthly. No credit checks or financing charges.</div>
                  </div>
                </div>
                <div class="plan-row-right">
                  <div class="plan-row-recurring">Recurring</div>
                </div>
              </div>

              <!-- Commitment Heading -->
              <h3 class="commitment-heading">Stay committed longer, save more.</h3>

              <!-- 3 Month Plan -->
              <div
                class="plan-row"
                :class="{ selected: paymentInfo.payment_terms === 3 }"
                @click="changePaymentTerms(3)"
              >
                <div class="plan-row-left">
                  <div class="glp1-radio-circle" :class="{ checked: paymentInfo.payment_terms === 3 }"></div>
                  <div class="plan-row-info">
                    <div class="plan-row-price-inline"><span class="price-amt" :class="{ 'price-loading': !pricesReady }"><strong>${{ formatMonthlyPrice(productPricing.terms['3'], 3).split('.')[0] }}</strong><sup>{{ formatMonthlyPrice(productPricing.terms['3'], 3).split('.')[1] }}</sup></span>/ mo. <span class="plan-duration">(3 mo plan)</span></div>
                  </div>
                </div>
                <div class="plan-row-right">
                  <div class="plan-row-today"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ formatPrice(productPricing.terms['3']) }}</span> today</div>
                </div>
              </div>

              <!-- 6 Month Plan -->
              <div
                class="plan-row"
                :class="{ selected: paymentInfo.payment_terms === 6 }"
                @click="changePaymentTerms(6)"
              >
                <span class="plan-badge plan-badge--popular">Popular</span>
                <div class="plan-row-left">
                  <div class="glp1-radio-circle" :class="{ checked: paymentInfo.payment_terms === 6 }"></div>
                  <div class="plan-row-info">
                    <div class="plan-row-price-inline"><span class="price-amt" :class="{ 'price-loading': !pricesReady }"><strong>${{ formatMonthlyPrice(productPricing.terms['6'], 6).split('.')[0] }}</strong><sup>{{ formatMonthlyPrice(productPricing.terms['6'], 6).split('.')[1] }}</sup></span>/ mo. <span class="plan-duration">(6 mo plan)</span></div>
                  </div>
                </div>
                <div class="plan-row-right">
                  <div class="plan-row-today"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ formatPrice(productPricing.terms['6']) }}</span> today</div>
                </div>
              </div>

              <!-- 12 Month Plan -->
              <div
                class="plan-row plan-row--with-savings"
                :class="{ selected: paymentInfo.payment_terms === 12 }"
                @click="changePaymentTerms(12)"
              >
                <span class="plan-badge plan-badge--recommended">Recommended</span>
                <div class="plan-row-left">
                  <div class="glp1-radio-circle" :class="{ checked: paymentInfo.payment_terms === 12 }"></div>
                  <div class="plan-row-info">
                    <div class="plan-row-price-inline"><span class="price-amt" :class="{ 'price-loading': !pricesReady }"><strong>${{ formatMonthlyPrice(productPricing.terms['12'], 12).split('.')[0] }}</strong><sup>{{ formatMonthlyPrice(productPricing.terms['12'], 12).split('.')[1] }}</sup></span>/ mo. <span class="plan-duration">(12 mo plan)</span></div>
                  </div>
                </div>
                <div class="plan-row-right">
                  <div class="plan-row-today"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ formatPrice(productPricing.terms['12']) }}</span> today</div>
                  <SummerSaleBadge />
                </div>
              </div>
            </div>

            <!-- Error message -->
            <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

            <!-- Continue Button -->
            <div class="form-actions">
              <button type="button" class="continue-btn" @click="proceedToCheckout" :disabled="isLoading">
                <span v-if="isLoading" class="btn-spinner"></span>
                <span v-else>Continue <span class="btn-arrow">→</span></span>
              </button>
            </div>
          </div>

          <!-- Right Column: Order Summary -->
          <div class="step-9-right">
            <div class="order-summary-card">
              <div class="order-summary-header">Order Summary</div>
              <div class="order-summary-line">
                <span>{{ selectedMedication === 'semaglutide' ? 'Semaglutide' : 'Tirzepatide' }}</span>
                <span class="order-summary-price price-amt" :class="{ 'price-loading': !pricesReady }">${{ getTotalOrderAmount().toFixed(2).split('.')[0] }}<sup>{{ getTotalOrderAmount().toFixed(2).split('.')[1] }}</sup></span>
              </div>
              <div class="order-summary-total">
                <span class="order-summary-due">Due today</span>
                <span class="order-summary-due-price price-amt" :class="{ 'price-loading': !pricesReady }">${{ getTotalOrderAmount().toFixed(2).split('.')[0] }}<sup>{{ getTotalOrderAmount().toFixed(2).split('.')[1] }}</sup></span>
              </div>
              <div class="order-summary-guarantee">
                <strong>Our guarantee:</strong> If you pay for a treatment and your provider determines you're not eligible, we'll issue a full refund
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Out of Stock Modal -->
    <div v-if="showOutOfStockModal" class="modal-overlay" @click="closeOutOfStockModal">
      <div class="modal-content" @click.stop>
        <div class="modal-body">
          <p class="modal-text">
            <strong>{{ currentMedicationType === 'mounjaro' ? 'Mounjaro' : 'Ozempic' }}</strong> is temporarily out of stock as we await our next shipment. You may choose an alternative medication now, or we'll notify you as soon as it becomes available.
          </p>
          <button class="modal-close-btn" @click="closeOutOfStockModal">Close</button>
        </div>
      </div>
    </div>
    <!-- Paid User Modal -->
    <PaidUserModal
      :visible="showPaidUserModal"
      :email="people.email"
      :reason="paidUserReason"
      :role="paidUserRole"
      @close="showPaidUserModal = false" />
  </div>
</template>

<script>
import { checkEmail, isValidEmailFormat } from '@/utils/checkEmail'
import { cleanUsPhone, cleanUsPhoneInput, formatUsPhone, isValidUsPhone } from '@/utils/phone'
import { isRestrictedState, RESTRICTED_STATE_MESSAGE, withoutRestrictedStates } from '@/utils/restrictedStates'
import PaidUserModal from '../common/PaidUserModal.vue'

export default {
  name: 'Glp1V4Wizard',

  components: {
    PaidUserModal
  },

  props: {
    baseUrl: {
      type: String,
      default: () => process.env.VUE_APP_API_ENDPOINT
    }
  },

  data() {
    return {
      currentStep: 1,
      totalSteps: 9,
      isSubmitting: false,
      emailCheckError: '',
      isCheckingEmail: false,
      showPaidUserModal: false,
      paidUserReason: 'paid_user',
      paidUserRole: '',
      errorMessage: '',
      smsConsentError: '',
      smsConsentShake: false,
      skipLogoAnimation: false,

      // Form field errors
      fieldErrors: {},

      // State autocomplete
      stateSearchText: '',
      showStateSuggestions: false,
      stateHighlightIndex: -1,

      // Custom dropdown states
      genderDropdownOpen: false,

      // GLP-1 status
      isTakingGLP1: null, // null = not answered, true = taking, false = not taking
      currentMedicationType: '', // semaglutide, tirzepatide, mounjaro, ozempic
      selectedDosage: '',
      selectedMedication: '', // Final medication choice (semaglutide or tirzepatide)
      showOutOfStockModal: false,
      isLoading: false,

      // Product configuration for cart
      productConfig: {
        semaglutide: {
          id: '675ac4cb6bf102c19ec3afec',
          name: 'Semaglutide',
          stripe_id: 'sema_plan_278928',
          image: 'semaglutide'
        },
        tirzepatide: {
          id: '675ac4cb6bf102c19ec3afe9',
          name: 'Tirzepatide',
          stripe_id: 'tirz_plan_1',
          image: 'tirzepatide'
        }
      },

      // Pricing configuration (env FALLBACK — live promo prices from the API override this)
      pricingConfig: JSON.parse(process.env.VUE_APP_PRICING || '{"semaglutide":{"monthly":{"first":99.97,"regular":194.97},"terms":{"3":449.91,"6":749.82,"12":1199.64}},"tirzepatide":{"monthly":{"first":174.97,"regular":274.97},"terms":{"3":644.91,"6":1169.82,"12":2099.64}}}'),

      // Live promo plans from /api/get-all-plans?isPromo=true, per medication then keyed by
      // payment term: { id, price }. Empty until loadPlans resolves; on failure it stays
      // empty so env pricingConfig remains the fallback.
      apiPlans: { semaglutide: {}, tirzepatide: {} },
      // Gates price display: false until the live fetch settles, so env prices never flash
      // before the live promo prices load in.
      pricesReady: false,

      // Payment info
      paymentInfo: {
        payment_terms: 12, // Default to 12 months (Best Value)
        amount: 0,
        discount: 0
      },

      // User data
      people: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        state: '',
        gender: '',
        dob: {
          mm: '',
          dd: '',
          yyyy: ''
        },
        height: {
          foot: '',
          inches: ''
        },
        weight: '',
        agree_to_phone: false
      },

      // All US States
      allStates: {
        'Alabama': 'AL',
        'Alaska': 'AK',
        'Arizona': 'AZ',
        'Arkansas': 'AR',
        'California': 'CA',
        'Colorado': 'CO',
        'Connecticut': 'CT',
        'Delaware': 'DE',
        'Florida': 'FL',
        'Georgia': 'GA',
        'Hawaii': 'HI',
        'Idaho': 'ID',
        'Illinois': 'IL',
        'Indiana': 'IN',
        'Iowa': 'IA',
        'Kansas': 'KS',
        'Kentucky': 'KY',
        'Louisiana': 'LA',
        'Maine': 'ME',
        'Maryland': 'MD',
        'Massachusetts': 'MA',
        'Michigan': 'MI',
        'Minnesota': 'MN',
        'Mississippi': 'MS',
        'Missouri': 'MO',
        'Montana': 'MT',
        'Nebraska': 'NE',
        'Nevada': 'NV',
        'New Hampshire': 'NH',
        'New Jersey': 'NJ',
        'New Mexico': 'NM',
        'New York': 'NY',
        'North Carolina': 'NC',
        'North Dakota': 'ND',
        'Ohio': 'OH',
        'Oklahoma': 'OK',
        'Oregon': 'OR',
        'Pennsylvania': 'PA',
        'Puerto Rico': 'PR',
        'Rhode Island': 'RI',
        'South Carolina': 'SC',
        'South Dakota': 'SD',
        'Tennessee': 'TN',
        'Texas': 'TX',
        'Utah': 'UT',
        'Vermont': 'VT',
        'Virginia': 'VA',
        'Washington': 'WA',
        'West Virginia': 'WV',
        'Wisconsin': 'WI',
        'Wyoming': 'WY'
      }
    }
  },

  async mounted() {
    // Load saved progress from session storage
    this.loadProgress()

    // Fetch live promo prices for both products (flips pricesReady when done)
    this.loadPlans()

    // Handle legacy step query parameter (remove from URL)
    if (this.$route.query.step) {
      this.$router.replace({ path: '/get-started' })
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  watch: {
    // Save progress when step changes
    currentStep(newStep) {
      this.saveProgress()
      // Reset animation flag when going back to step 1
      if (newStep === 1) {
        this.skipLogoAnimation = false
      }
    },

    // Auto-save when form data changes
    people: {
      deep: true,
      handler() {
        this.saveProgress()
      }
    },

    selectedMedication() {
      this.saveProgress()
    },

    selectedDosage() {
      this.saveProgress()
    },

    currentMedicationType() {
      this.saveProgress()
    },

    isTakingGLP1() {
      this.saveProgress()
    },

    'paymentInfo.payment_terms'() {
      this.saveProgress()
    }
  },

  computed: {
    lowestSemaPrice() {
      const total = this.getMergedPricing('semaglutide').terms['12']
      const monthly = total / 12
      return { dollars: Math.floor(monthly), cents: Math.round((monthly % 1) * 100).toString().padStart(2, '0') }
    },
    lowestTirzPrice() {
      const total = this.getMergedPricing('tirzepatide').terms['12']
      const monthly = total / 12
      return { dollars: Math.floor(monthly), cents: Math.round((monthly % 1) * 100).toString().padStart(2, '0') }
    },
    filteredStates() {
      const search = this.stateSearchText.toLowerCase().trim()
      // allStates stays complete so a typed "Louisiana" still resolves to LA
      // and can be caught by the gate. It must never be SUGGESTED, though —
      // strip the restricted states before the list reaches the customer.
      const states = withoutRestrictedStates(
        Object.entries(this.allStates).map(([name, abbr]) => ({ name, abbr }))
      )
      if (!search) return states
      return states.filter(s =>
        s.name.toLowerCase().includes(search) || s.abbr.toLowerCase() === search
      )
    },
    bmiCalculated() {
      const heightInInches = (parseInt(this.people.height.foot) * 12) + parseInt(this.people.height.inches)
      const weightInPounds = parseInt(this.people.weight)
      if (heightInInches && weightInPounds) {
        return ((weightInPounds * 703) / (heightInInches * heightInInches)).toFixed(1)
      }
      return ''
    },
    phoneDisplay: {
      get() {
        // Show an optional "1 " country code, e.g. "1 (555) 555-5555"
        return formatUsPhone(this.people.phone)
      },
      set(value) {
        // Keep an optional leading 1 country code; cap national part at 10
        this.people.phone = cleanUsPhoneInput(value)
      }
    },
    // Pricing computed properties (merged: live promo prices override env fallback)
    productPricing() {
      return this.getMergedPricing(this.selectedMedication === 'semaglutide' ? 'semaglutide' : 'tirzepatide')
    },
    firstMonthPrice() {
      return this.productPricing?.monthly?.first || 0
    },
    regularMonthlyPrice() {
      return this.productPricing?.monthly?.regular || 0
    }
  },

  methods: {
    async checkEmailOnBlur() {
      this.emailCheckError = ''
      if (!this.people.email || !isValidEmailFormat(this.people.email)) {
        return
      }
      this.isCheckingEmail = true
      try {
        const result = await checkEmail(this.people.email)
        if (!result.proceed) {
          if (result.reason === 'paid_user') {
            this.paidUserReason = 'paid_user'
            this.paidUserRole = ''
            this.showPaidUserModal = true
            this.emailCheckError = 'This email already has a paid account.'
          } else if (result.reason === 'wrong_role') {
            this.paidUserReason = 'wrong_role'
            this.paidUserRole = result.role || ''
            this.showPaidUserModal = true
            this.emailCheckError = `Access denied. Account role (${result.role}) is not authorized.`
          }
        }
      } catch (e) {
        // Never block on error
      } finally {
        this.isCheckingEmail = false
      }
    },
    async handleEmailCheck() {
      this.emailCheckError = ''
      if (!isValidEmailFormat(this.people.email)) {
        return true // Let normal validation handle format errors
      }
      this.isCheckingEmail = true
      try {
        const result = await checkEmail(this.people.email)
        if (!result.proceed) {
          if (result.reason === 'paid_user') {
            this.paidUserReason = 'paid_user'
            this.paidUserRole = ''
            this.showPaidUserModal = true
            this.emailCheckError = 'This email already has a paid account.'
          } else if (result.reason === 'wrong_role') {
            this.paidUserReason = 'wrong_role'
            this.paidUserRole = result.role || ''
            this.showPaidUserModal = true
            this.emailCheckError = `Access denied. Account role (${result.role}) is not authorized.`
          }
          return false
        }
        return true
      } catch (e) {
        return true // Never block on error
      } finally {
        this.isCheckingEmail = false
      }
    },
    // Save checkout progress to session storage
    saveProgress() {
      try {
        const progressData = {
          currentStep: this.currentStep,
          people: this.people,
          selectedMedication: this.selectedMedication,
          selectedDosage: this.selectedDosage,
          currentMedicationType: this.currentMedicationType,
          isTakingGLP1: this.isTakingGLP1,
          paymentInfo: this.paymentInfo,
          timestamp: Date.now()
        }
        sessionStorage.setItem('glp1V4WizardProgress', JSON.stringify(progressData))
      } catch (error) {
        console.error('Error saving progress:', error)
      }
    },

    // Load checkout progress from session storage
    loadProgress() {
      try {
        const savedProgress = sessionStorage.getItem('glp1V4WizardProgress')
        if (savedProgress) {
          const progressData = JSON.parse(savedProgress)

          // Only restore if saved within last 24 hours
          const dayInMs = 24 * 60 * 60 * 1000
          if (Date.now() - progressData.timestamp < dayInMs) {
            const restoredStep = progressData.currentStep || 1
            this.currentStep = restoredStep
            // Skip logo animation only when returning to step 9 (from checkout)
            if (restoredStep === 9) {
              this.skipLogoAnimation = true
            }
            this.selectedMedication = progressData.selectedMedication || ''
            this.selectedDosage = progressData.selectedDosage || ''
            this.currentMedicationType = progressData.currentMedicationType || ''
            this.isTakingGLP1 = progressData.isTakingGLP1
            if (progressData.paymentInfo) {
              this.paymentInfo = {
                ...this.paymentInfo,
                ...progressData.paymentInfo
              }
            }
            if (progressData.people) {
              this.people = {
                ...this.people,
                ...progressData.people
              }
              // Sync state text with restored abbreviation
              if (this.people.state) {
                this.stateSearchText = this.getStateName(this.people.state)
              }
            }
            console.log('Progress restored from session storage')
          } else {
            // Clear expired data
            sessionStorage.removeItem('glp1V4WizardProgress')
          }
        }
      } catch (error) {
        console.error('Error loading progress:', error)
      }
    },

    // Clear saved progress
    clearProgress() {
      try {
        sessionStorage.removeItem('glp1V4WizardProgress')
      } catch (error) {
        console.error('Error clearing progress:', error)
      }
    },

    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++
        this.errorMessage = ''
        this.fieldErrors = {}
        window.scrollTo(0, 0)
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        // If on step 7 and user is not taking GLP-1, skip back to step 4
        if (this.currentStep === 7 && this.isTakingGLP1 === false) {
          this.currentStep = 4
        } else {
          this.currentStep--
        }
        this.errorMessage = ''
        this.fieldErrors = {}
        window.scrollTo(0, 0)
      }
    },

    // Custom dropdown helpers
    getStateName(abbr) {
      for (const [name, stateAbbr] of Object.entries(this.allStates)) {
        if (stateAbbr === abbr) return name
      }
      return abbr
    },

    // State autocomplete methods
    onStateInput(value) {
      this.stateSearchText = value
      this.showStateSuggestions = true
      this.stateHighlightIndex = -1
      this.fieldErrors.state = false

      // Check if typed value exactly matches a state name or abbreviation
      const match = Object.entries(this.allStates).find(([name, abbr]) =>
        name.toLowerCase() === value.trim().toLowerCase() || abbr.toLowerCase() === value.trim().toLowerCase()
      )
      this.people.state = match ? match[1] : ''
    },

    onStateFocus() {
      // Don't show custom suggestions on focus — let browser autofill work
      // Suggestions appear only when user starts typing (via onStateInput)
    },

    onStateBlur() {
      setTimeout(() => {
        this.showStateSuggestions = false
        // Try to resolve typed text to a state on blur
        if (this.stateSearchText && !this.people.state) {
          const match = Object.entries(this.allStates).find(([name, abbr]) =>
            name.toLowerCase() === this.stateSearchText.trim().toLowerCase() ||
            abbr.toLowerCase() === this.stateSearchText.trim().toLowerCase()
          )
          if (match) {
            this.people.state = match[1]
            this.stateSearchText = match[0]
          }
        }
      }, 150)
    },

    onStateKeyDown() {
      if (this.filteredStates.length > 0) {
        this.stateHighlightIndex = Math.min(this.stateHighlightIndex + 1, this.filteredStates.length - 1)
        this.scrollStateHighlightIntoView()
      }
    },

    onStateKeyUp() {
      if (this.stateHighlightIndex > 0) {
        this.stateHighlightIndex--
        this.scrollStateHighlightIntoView()
      }
    },

    onStateEnter() {
      if (this.stateHighlightIndex >= 0 && this.filteredStates[this.stateHighlightIndex]) {
        this.selectState(this.filteredStates[this.stateHighlightIndex].abbr)
      }
    },

    onStateEscape() {
      this.showStateSuggestions = false
      if (this.$refs.stateInput) this.$refs.stateInput.blur()
    },

    scrollStateHighlightIntoView() {
      this.$nextTick(() => {
        const highlighted = this.$el.querySelector('.state-suggestion-item.is-highlighted')
        if (highlighted) highlighted.scrollIntoView({ block: 'nearest' })
      })
    },

    selectState(abbr) {
      this.people.state = abbr
      this.stateSearchText = this.getStateName(abbr)
      this.showStateSuggestions = false
      this.stateHighlightIndex = -1
      this.fieldErrors.state = false
    },

    selectGender(gender) {
      this.people.gender = gender
      this.genderDropdownOpen = false
      this.fieldErrors.gender = false
    },

    handleClickOutside(event) {
      // Check if click is inside any dropdown
      const clickedDropdown = event.target.closest('.custom-dropdown')

      // Close gender dropdown if clicking outside it
      if (this.genderDropdownOpen && !clickedDropdown) {
        this.genderDropdownOpen = false
      }
    },

    // Step 1 validation and submit
    async handleStep1Submit() {
      this.fieldErrors = {}
      this.errorMessage = ''

      if (!this.people.state) {
        this.fieldErrors.state = true
        this.errorMessage = 'Please select your state'
        return
      }
      // GLP-1 medication cannot be sold into Louisiana or Mississippi. The
      // suggestion list already hides them, but the field is free text, so
      // this is the gate that actually stops the flow.
      if (isRestrictedState(this.people.state)) {
        this.fieldErrors.state = true
        this.errorMessage = RESTRICTED_STATE_MESSAGE
        return
      }
      if (!this.people.gender) {
        this.fieldErrors.gender = true
        this.errorMessage = 'Please select your sex assigned at birth'
        return
      }
      if (!this.people.dob.mm || !this.people.dob.dd || !this.people.dob.yyyy) {
        if (!this.people.dob.mm) this.fieldErrors.dob_mm = true
        if (!this.people.dob.dd) this.fieldErrors.dob_dd = true
        if (!this.people.dob.yyyy) this.fieldErrors.dob_yyyy = true
        this.errorMessage = 'Please enter your complete date of birth'
        return
      }

      // Validate DOB
      const month = parseInt(this.people.dob.mm)
      const day = parseInt(this.people.dob.dd)
      const year = parseInt(this.people.dob.yyyy)

      if (month < 1 || month > 12) {
        this.fieldErrors.dob_mm = true
        this.errorMessage = 'Please enter a valid month (01-12)'
        return
      }
      if (day < 1 || day > 31) {
        this.fieldErrors.dob_dd = true
        this.errorMessage = 'Please enter a valid day (01-31)'
        return
      }
      if (year < 1900 || year > new Date().getFullYear()) {
        this.fieldErrors.dob_yyyy = true
        this.errorMessage = 'Please enter a valid year'
        return
      }

      // Check age (must be 18+)
      const birthDate = new Date(year, month - 1, day)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      if (age < 18) {
        this.errorMessage = 'You must be at least 18 years old to use this service'
        return
      }

      // Fire questionnaire_start on Step 1 submission
      try {
        if (window.tracking) {
          await window.tracking.assertSession()
          window.tracking.event('questionnaire_start', { funnel_step: 1, form: "get_started" })
        }
      } catch (error) {
        // tracking errors are non-fatal
      }

      if (window.tracking) {
        window.tracking.event('state', { funnel_step: 2, form: "get_started" })
        window.tracking.event('sex_at_birth', { funnel_step: 2, form: "get_started" })
        window.tracking.event('dob_added', { funnel_step: 2, form: "get_started" })
      }

      this.nextStep()
    },

    // Step 2 validation and submit
    handleStep2Submit() {
      this.fieldErrors = {}
      this.errorMessage = ''

      if (!this.people.height.foot) {
        this.fieldErrors.height_foot = true
        this.errorMessage = 'Please enter your height in feet'
        return
      }
      if (!this.people.height.inches && this.people.height.inches !== '0') {
        this.fieldErrors.height_inches = true
        this.errorMessage = 'Please enter your height in inches'
        return
      }
      if (!this.people.weight) {
        this.fieldErrors.weight = true
        this.errorMessage = 'Please enter your weight'
        return
      }

      if (window.tracking) {
        window.tracking.event('height', { funnel_step: 3, form: "get_started" })
        window.tracking.event('current_weight', { funnel_step: 3, form: "get_started" })
        window.tracking.event('bmi_confirmation', { funnel_step: 3, form: "get_started" })
      }

      this.nextStep()
    },

    triggerSmsConsentShake() {
      this.smsConsentShake = false
      this.$nextTick(() => {
        this.smsConsentShake = true
        setTimeout(() => {
          this.smsConsentShake = false
        }, 700)
      })
    },

    // Step 3 validation and submit
    async handleStep3Submit() {
      this.fieldErrors = {}
      this.errorMessage = ''
      this.smsConsentError = ''

      if (!this.people.first_name.trim()) {
        this.fieldErrors.first_name = true
        this.errorMessage = 'Please enter your first name'
        return
      }
      if (!this.people.last_name.trim()) {
        this.fieldErrors.last_name = true
        this.errorMessage = 'Please enter your last name'
        return
      }
      if (!this.people.email.trim()) {
        this.fieldErrors.email = true
        this.errorMessage = 'Please enter your email address'
        return
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.people.email)) {
        this.fieldErrors.email = true
        this.errorMessage = 'Please enter a valid email address'
        return
      }
      // Check if email already exists
      const emailCheckPassed = await this.handleEmailCheck()
      if (!emailCheckPassed) {
        return
      }
      if (!isValidUsPhone(this.people.phone)) {
        this.fieldErrors.phone = true
        this.errorMessage = 'Please enter a valid US phone number'
        return
      }

      if (!this.people.agree_to_phone) {
        this.smsConsentError = 'Please agree to receive SMS communications to continue'
        this.triggerSmsConsentShake()
        return
      }

      // Submit to API to create lead/account
      try {
        this.isSubmitting = true

        // Add user agent and tracking
        this.people.user_agent = window.navigator.userAgent
        try {
          const sessionId = await window.tracking.assertSession()
          if (sessionId) {
            this.people.tracking_session_id = sessionId
          }
        } catch(error) {
          if (window.trackingSessionId) {
            this.people.tracking_session_id = window.trackingSessionId
          }
        }

        // Calculate height in inches for backend
        const feet = parseInt(this.people.height.foot) || 0
        const inches = parseInt(this.people.height.inches) || 0
        this.people.heightInInches = (feet * 12) + inches

        // Format DOB as MM/DD/YYYY for backend
        const paddedMonth = this.people.dob.mm.padStart(2, '0')
        const paddedDay = this.people.dob.dd.padStart(2, '0')
        this.people.dobMMDDYYYY = `${paddedMonth}/${paddedDay}/${this.people.dob.yyyy}`

        // Normalize to 10 national digits (drops leading 1 country code)
        const cleanedPhone = cleanUsPhone(this.people.phone)

        const response = await this.$axios.post(`${this.baseUrl}/api/intake-form`, {
          ...this.people,
          phone: cleanedPhone
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (response.data.status === 'success') {
          this.people.id = response.data.user.id
          this.$store.dispatch('doLogin', response.data)
          if (window.tracking) {
            window.tracking.event('name_added', { funnel_step: 4, form: "get_started" })
            window.tracking.event('phone_added', { funnel_step: 4, form: "get_started" })
            window.tracking.event('email_complete', { funnel_step: 4, form: "get_started" })
            window.tracking.event('partial', {
              funnel_step: 5,
              event_id: crypto.randomUUID(),
              form: "get_started"
            })
          }
          this.nextStep()
        } else {
          this.errorMessage = response.data.message || 'There was an error. Please try again.'
        }
      } catch (error) {
        console.error('Form submission error:', error)
        if (error.response && error.response.data && error.response.data.message) {
          this.errorMessage = error.response.data.message
        } else {
          this.errorMessage = 'There was an error submitting your form. Please try again.'
        }
      } finally {
        this.isSubmitting = false
      }
    },

    // Auto-advance DOB fields
    autoAdvanceDob(field) {
      if (field === 'mm' && this.people.dob.mm.length === 2) {
        this.$refs.dobDd.focus()
      } else if (field === 'dd' && this.people.dob.dd.length === 2) {
        this.$refs.dobYyyy.focus()
      }
    },

    // Filter input to only allow numbers
    filterNumericInput(field) {
      if (field === 'height.foot') {
        this.people.height.foot = this.people.height.foot.replace(/\D/g, '')
      } else if (field === 'height.inches') {
        this.people.height.inches = this.people.height.inches.replace(/\D/g, '')
      } else if (field === 'weight') {
        this.people.weight = this.people.weight.replace(/\D/g, '')
      }
    },

    // Filter DOB input to only allow valid numbers
    filterDobInput(field) {
      if (field === 'mm') {
        this.people.dob.mm = this.people.dob.mm.replace(/\D/g, '')
        const val = parseInt(this.people.dob.mm)
        if (val > 12) this.people.dob.mm = '12'
        if (this.people.dob.mm.length === 2 && val === 0) this.people.dob.mm = '01'
      } else if (field === 'dd') {
        this.people.dob.dd = this.people.dob.dd.replace(/\D/g, '')
        const val = parseInt(this.people.dob.dd)
        if (val > 31) this.people.dob.dd = '31'
        if (this.people.dob.dd.length === 2 && val === 0) this.people.dob.dd = '01'
      } else if (field === 'yyyy') {
        this.people.dob.yyyy = this.people.dob.yyyy.replace(/\D/g, '')
        if (this.people.dob.yyyy.length === 4) {
          const val = parseInt(this.people.dob.yyyy)
          const currentYear = new Date().getFullYear()
          if (val > currentYear) this.people.dob.yyyy = String(currentYear)
          if (val < 1900) this.people.dob.yyyy = '1900'
        }
      }
    },

    // Clamp inches to 0-11
    clampInches() {
      const inches = parseInt(this.people.height.inches)
      if (inches > 11) {
        this.people.height.inches = '11'
      }
    },

    // Clamp feet to 3-8
    clampFeet() {
      const feet = parseInt(this.people.height.foot)
      if (feet > 8) {
        this.people.height.foot = '8'
      } else if (this.people.height.foot.length >= 1 && feet < 3) {
        this.people.height.foot = '3'
      }
    },

    // Clamp weight to reasonable range (max 999 lbs)
    clampWeight() {
      this.people.weight = this.people.weight.replace(/\D/g, '')
      const weight = parseInt(this.people.weight)
      if (weight > 999) this.people.weight = '999'
    },

    // Auto-advance height fields
    autoAdvanceHeight(field) {
      if (field === 'feet' && this.people.height.foot.length === 1) {
        this.$nextTick(() => {
          const inchesField = document.getElementById('height-inches-v4')
          if (inchesField) inchesField.focus()
        })
      } else if (field === 'inches' && this.people.height.inches.length === 2) {
        this.$nextTick(() => {
          const weightField = document.getElementById('weight-pounds-v4')
          if (weightField) weightField.focus()
        })
      }
    },

    // Format phone number for display
    formatPhoneNumber(phone) {
      if (!phone) return ''
      const cleaned = phone.replace(/\D/g, '')
      if (cleaned.length >= 6) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`
      } else if (cleaned.length >= 3) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
      }
      return cleaned
    },

    handlePhoneInput(event) {
      // Cap to 10 digits, then force the displayed value so extra
      // keystrokes can't linger past the cap.
      this.people.phone = cleanUsPhoneInput(event.target.value)
      event.target.value = formatUsPhone(this.people.phone)
      if (this.errorMessage) this.errorMessage = ''
      if (this.fieldErrors.phone) this.$set(this.fieldErrors, 'phone', false)
    },

    // Step 4: GLP-1 selection
    handleGLP1Selection(isTaking) {
      this.isTakingGLP1 = isTaking
      this.errorMessage = ''

      if (window.tracking) {
        window.tracking.event('glp1_status', { funnel_step: 6, form: "get_started" })
      }

      // Advance to next step after short delay for visual feedback
      setTimeout(() => {
        if (isTaking) {
          // Taking GLP-1: go to step 5 (which medication?)
          this.nextStep()
        } else {
          // Not taking: clear any previous selections and skip to step 7
          this.currentMedicationType = ''
          this.selectedDosage = ''
          this.currentStep = 7
          window.scrollTo(0, 0)
        }
      }, 300)
    },

    // Step 5: Medication selection (what they're currently taking)
    handleCurrentMedicationSelection(medication) {
      this.currentMedicationType = medication

      if (window.tracking) {
        window.tracking.event('prior_medications', { funnel_step: 7, form: "get_started" })
      }

      // Advance to dosage selection after brief delay
      setTimeout(() => {
        this.nextStep()
      }, 300)
    },

    closeOutOfStockModal() {
      this.showOutOfStockModal = false
      this.currentMedicationType = '' // Reset selection
    },

    // Show out of stock modal for brand medications (Mounjaro/Ozempic)
    showBrandOutOfStock(brand) {
      this.currentMedicationType = brand
      this.showOutOfStockModal = true
    },

    // Step 6: Dosage selection
    selectDosage(type, dose) {
      this.selectedDosage = dose

      if (window.tracking) {
        window.tracking.event('medication_dose', { funnel_step: 8, form: "get_started" })
      }

      // Advance to treatment selection after brief delay
      setTimeout(() => {
        this.nextStep()
      }, 300)
    },

    // Step 7: Treatment selection
    async selectMedication(medication) {
      // Dose mapping when switching from Semaglutide to Tirzepatide
      const semaToTirzMapping = {
        0.25: 2.5,
        0.5: 2.5,
        1.0: 5.0,
        1.5: 7.5,
        2.0: 7.5,
        2.5: 10.0
      }

      this.selectedMedication = medication

      // If not currently taking any GLP-1, set to lowest dose
      if (!this.currentMedicationType || this.currentMedicationType === '') {
        this.selectedDosage = medication === 'tirzepatide' ? '2.5 mg' : '0.25 mg'
      }
      // Switching from Semaglutide/Ozempic to Tirzepatide
      else if (medication === 'tirzepatide' &&
        (this.currentMedicationType === 'semaglutide' || this.currentMedicationType === 'ozempic') &&
        this.selectedDosage) {
        const currentDose = parseFloat(this.selectedDosage)
        const mappedDose = semaToTirzMapping[currentDose] || 2.5
        this.selectedDosage = `${mappedDose} mg`
      }
      // Switching from Tirzepatide/Mounjaro to Semaglutide - start at lowest
      else if (medication === 'semaglutide' &&
        (this.currentMedicationType === 'tirzepatide' || this.currentMedicationType === 'mounjaro')) {
        this.selectedDosage = '0.25 mg'
      }

      if (window.tracking) {
        window.tracking.event('treatment_selection', { funnel_step: 9, form: "get_started" })
      }

      // Advance to next step
      this.nextStep()
    },

    // Pricing methods
    async loadPlans() {
      try {
        const entries = await Promise.all(
          Object.keys(this.productConfig).map(async (med) => {
            const productId = this.productConfig[med].id
            const response = await this.$axios.get(`${this.baseUrl}/api/get-all-plans`, {
              params: { isPromo: true, productId }
            })
            const plans = {}
            if (response.data.status === 'success' && Array.isArray(response.data.plans)) {
              response.data.plans.forEach((plan) => {
                if (plan && plan.terms != null && plan.price != null) {
                  plans[Number(plan.terms)] = { id: plan.id, price: Number(plan.price) }
                }
              })
            }
            return [med, plans]
          })
        )
        const next = { semaglutide: {}, tirzepatide: {} }
        entries.forEach(([med, plans]) => { next[med] = plans })
        this.apiPlans = next
      } catch (error) {
        console.error('Error loading plans:', error)
      } finally {
        this.pricesReady = true
      }
    },
    // Merge live promo prices over env pricing so missing terms keep the fallback.
    // Mirrors the term-total semantics of Checkout-glp1-v4's getProductPricing.
    getMergedPricing(medication) {
      const base = this.pricingConfig[medication] || this.pricingConfig.tirzepatide
      const live = this.apiPlans[medication] || {}
      const pricing = { monthly: { ...base.monthly }, terms: { ...base.terms } }
      if (live[1]) {
        pricing.monthly = { first: live[1].price, regular: live[1].price }
      }
      [3, 6, 12].forEach((term) => {
        if (live[term]) pricing.terms[term] = live[term].price
      })
      return pricing
    },
    getTotalPrice(term) {
      if (term === 1) {
        return parseFloat(this.firstMonthPrice.toFixed(2))
      }
      return parseFloat((this.productPricing?.terms?.[term] || 0).toFixed(2))
    },

    getCurrentPrice() {
      if (this.paymentInfo.payment_terms === 1) {
        return parseFloat(this.firstMonthPrice.toFixed(2))
      }
      return parseFloat(this.getTotalPrice(this.paymentInfo.payment_terms).toFixed(2))
    },

    getTotalOrderAmount() {
      const basePrice = this.getCurrentPrice()
      const discount = this.paymentInfo.discount || 0
      return Math.max(0, basePrice - discount)
    },

    changePaymentTerms(value) {
      this.paymentInfo.payment_terms = value
    },

    // Price formatting helpers
    formatPrice(price) {
      return price.toFixed(2)
    },

    formatMonthlyPrice(total, months) {
      return (total / months).toFixed(2)
    },

    getSavings(term) {
      // Calculate savings compared to paying monthly at regular price (live-merged)
      const regularMonthly = this.productPricing.monthly.regular
      const fullPrice = regularMonthly * term
      const termPrice = this.productPricing.terms[term.toString()]
      return fullPrice - termPrice
    },

    // Step 8 continue
    advanceStep() {
      if (window.tracking) {
        window.tracking.event('inclusions_viewed', { funnel_step: 10, form: "get_started" })
      }
      this.nextStep()
    },

    // Step 9 - Select product and proceed to checkout
    selectProductAndCheckout(medication) {
      this.selectedMedication = medication
      // Set default dosage based on medication
      if (!this.selectedDosage) {
        this.selectedDosage = medication === 'tirzepatide' ? '2.5 mg' : '0.25 mg'
      }
      this.proceedToCheckout()
    },

    async proceedToCheckout() {
      this.isLoading = true
      this.errorMessage = ''

      // Save wizard state for checkout pre-fill
      sessionStorage.setItem('glp1WizardState', JSON.stringify({
        currentStep: 9,
        selectedMedication: this.selectedMedication,
        selectedDosage: this.selectedDosage,
        payment_terms: this.paymentInfo.payment_terms,
        isTakingGLP1: this.isTakingGLP1,
        // User data for checkout pre-fill
        email: this.people.email,
        state: this.people.state,
        first_name: this.people.first_name,
        last_name: this.people.last_name,
        phone: cleanUsPhone(this.people.phone)
      }))

      try {
        const product = this.productConfig[this.selectedMedication]
        const term = this.paymentInfo.payment_terms
        const cartItem = {
          id: product.id,
          name: product.name,
          category: 'Weight Loss',
          price: this.getTotalPrice(term),
          retail_price: this.getTotalPrice(term),
          subheader: 'Compound Medication',
          sku: 1,
          stripe_id: product.stripe_id,
          quantity: 1,
          image: `https://newselfrx.s3.us-east-1.amazonaws.com/public/assets/product_image/webp/${product.image}/1.webp`,
          is_addon: false,
          sema_offer: 'sema_plan_278928',
          tir_offer: 'tirz_plan_1',
          payment_terms: term,
          dosage: this.selectedDosage
        }

        const response = await this.$axios.post(`${this.baseUrl}/api/save-cart`, cartItem)

        if (response.data.status === 'success') {
          // Clear old cart items first, then add the new one
          this.$store.dispatch('clearCart')
          this.$store.dispatch('addToCart', cartItem)
          this.$store.dispatch('setCartVisibility', false)

          if (window.tracking) {
            window.tracking.event('questionnaire_end', { funnel_step: 11, form: "get_started" })
            window.tracking.event('add_to_cart', {
              funnel_step: 12,
              event_value: cartItem.price,
              currency: 'USD',
              product_id: cartItem.id,
              product_name: cartItem.name,
              plan_id: cartItem.stripe_id,
              quantity: 1,
              form: "get_started"
            })
          }

          this.$router.push({
            name: 'CheckoutGlp1V4',
            params: { cart_id: response.data.id }
          })
        } else {
          this.errorMessage = 'Failed to create cart. Please try again.'
        }
      } catch (error) {
        console.error('Cart creation error:', error)
        this.errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.'
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* Price skeleton shimmer — hides env prices until live promo prices load */
.price-amt {
  display: inline-block;
}
.price-loading {
  color: transparent !important;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  min-width: 3ch;
}
.price-loading sup,
.price-loading strong {
  color: transparent !important;
}
.price-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #e9e9e9 25%, #f5f5f5 50%, #e9e9e9 75%);
  background-size: 200% 100%;
  animation: price-shimmer 1.1s ease-in-out infinite;
}
@keyframes price-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Base Variables */
.wizard-page {
  --ns-lime: #e9ff86;
  --ns-teal: #08466f;
  --ns-soft-blue: #c9e8ff;
  --ns-green: #10b981;
  --ns-gray-50: #f9fafb;
  --ns-gray-100: #f3f4f6;
  --ns-gray-200: #e5e7eb;
  --ns-gray-300: #d1d5db;
  --ns-gray-400: #9ca3af;
  --ns-gray-500: #6b7280;
  --ns-gray-600: #4b5563;
  --ns-gray-700: #374151;
  --ns-gray-800: #1f2937;
  --ns-gray-900: #111827;

  min-height: 100vh;
  background-color: var(--ns-gray-50);
  font-family: Satoshi, Arial, sans-serif;
  overflow-x: hidden;
  width: 100%;
}

.wizard-page *,
.wizard-page *::before,
.wizard-page *::after {
  box-sizing: border-box;
}

/* Header */
.wizard-header {
  background: white;
  border-bottom: 1px solid var(--ns-gray-200);
  width: 100%;
}

.header-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  position: relative;
}

.header-back-btn {
  position: absolute;
  left: 0;
  width: 28px;
  height: 28px;
  border: 1px solid var(--ns-gray-300);
  border-radius: 6px;
  background: white;
  color: var(--ns-gray-400);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}

/* Back button enter/leave transitions */
.back-btn-fade-enter-active,
.back-btn-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.back-btn-fade-enter,
.back-btn-fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.header-back-btn span {
  display: block;
  margin-top: -2px;
}

.header-back-btn:hover {
  background: var(--ns-gray-50);
  color: var(--ns-gray-600);
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.logo {
  height: 32px;
  width: auto;
  margin-left: 0;
  transition: margin-left 0.3s ease-out;
}

.logo.no-transition {
  transition: none;
}

.logo.logo-shifted {
  margin-left: 40px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.phone-info {
  text-align: right;
  line-height: 1.3;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.phone-icon-mobile {
  display: none;
  width: 24px;
  height: 24px;
  color: var(--ns-teal);
}

.phone-text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.phone-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ns-gray-500);
  line-height: 16px;
  margin: 0 0 2px 0;
}

.phone-number {
  font-size: 14px;
  font-weight: 600;
  color: var(--ns-teal);
  text-decoration: none;
  display: block;
}

.secure-badge {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: var(--ns-gray-50);
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid var(--ns-gray-200);
}

.lock-icon {
  width: 20px;
  height: 20px;
  color: var(--ns-teal);
}

.secure-text-group {
  display: flex;
  flex-direction: column;
}

.secure-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0;
  line-height: 1.2;
}

.payment-text {
  font-size: 12px;
  font-weight: 400;
  color: var(--ns-gray-500);
  margin: 0;
  line-height: 1.2;
}

/* Step Indicator */
.progress-bar {
  background: white;
  border-bottom: 1px solid var(--ns-gray-100);
  padding: 24px 16px;
  display: flex;
  justify-content: center;
  width: 100%;
}

.progress-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-wrapper {
  display: flex;
  align-items: center;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--ns-gray-300);
  background: white;
  flex-shrink: 0;
}

.step-circle.completed {
  background: var(--ns-teal);
  border-color: var(--ns-teal);
}

.step-circle.active {
  border-color: var(--ns-teal);
  background: white;
}

.check-icon {
  width: 14px;
  height: 14px;
  color: white;
}

.step-dot {
  width: 10px;
  height: 10px;
  background: var(--ns-teal);
  border-radius: 50%;
  animation: jellyPop 0.4s ease-out;
}

@keyframes jellyPop {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.4);
  }
  75% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

.step-line {
  width: 24px;
  height: 2px;
  background: var(--ns-gray-200);
  flex-shrink: 0;
}

.step-line.completed {
  background: var(--ns-teal);
}

/* Option Cards (Step 4, 5, 6) */
.option-cards .option-card:nth-child(1) {
  animation: slideUp 0.4s ease-out 0.1s both;
}

.option-cards .option-card:nth-child(2) {
  animation: slideUp 0.4s ease-out 0.25s both;
}

.option-cards .option-card:nth-child(3) {
  animation: slideUp 0.4s ease-out 0.4s both;
}

.option-cards .option-card:nth-child(4) {
  animation: slideUp 0.4s ease-out 0.55s both;
}

.option-cards .option-card:nth-child(5) {
  animation: slideUp 0.4s ease-out 0.7s both;
}

.option-cards .option-card:nth-child(6) {
  animation: slideUp 0.4s ease-out 0.85s both;
}

.option-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 2px solid var(--ns-gray-200);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.option-card:hover {
  border-color: var(--ns-gray-300);
}

.option-card.selected {
  border-color: var(--ns-teal);
  background: rgba(201, 232, 255, 0.2);
}

.option-radio {
  width: 24px;
  height: 24px;
  border: 2px solid var(--ns-gray-300);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}

.option-radio.checked {
  border-color: var(--ns-teal);
}

.radio-dot {
  width: 12px;
  height: 12px;
  background: var(--ns-teal);
  border-radius: 50%;
  animation: jellyPop 0.3s ease-out;
}

.option-text {
  font-size: 16px;
  color: var(--ns-gray-700);
  line-height: 1.4;
}

.option-text strong {
  color: var(--ns-teal);
}

/* Main Content */
.wizard-main {
  max-width: 600px;
  margin: 0 auto;
  padding: 48px 24px;
}

.wizard-main-wide {
  max-width: 900px;
}

.wizard-main:has(.step-9-layout) {
  max-width: 960px;
}

.wizard-content {
  /* Staggered animation container */
  animation-duration: 0s;
}

.step-header {
  animation: slideUp 0.4s ease-out both;
}

.wizard-form .form-group:nth-child(1) {
  animation: slideUp 0.4s ease-out 0.1s both;
}

.wizard-form .form-group:nth-child(2) {
  animation: slideUp 0.4s ease-out 0.15s both;
}

.wizard-form .form-group:nth-child(3) {
  animation: slideUp 0.4s ease-out 0.2s both;
}

.wizard-form .form-group:nth-child(4) {
  animation: slideUp 0.4s ease-out 0.25s both;
}

.wizard-form .form-actions {
  animation: slideUp 0.4s ease-out 0.3s both;
}

.wizard-form .trust-row {
  animation: slideUp 0.4s ease-out 0.35s both;
}

.wizard-form .checkbox-label {
  animation: slideUp 0.4s ease-out 0.35s both;
}

.wizard-form .consent-text {
  animation: slideUp 0.4s ease-out 0.4s both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Step Header */
.step-header {
  text-align: center;
  margin-bottom: 32px;
}

.step-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--ns-teal);
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.step-subtitle {
  font-size: 16px;
  color: var(--ns-gray-500);
  margin: 0;
}

/* Form Styles */
.wizard-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
}

.form-group.dropdown-open {
  z-index: 9999;
}

.form-group:has(.state-autocomplete:focus-within) {
  z-index: 99999;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--ns-gray-700);
}

.form-group input,
.form-group select {
  padding: 14px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  font-size: 15px;
  color: var(--ns-gray-900);
  background: white;
  outline: none;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #08466f;
  box-shadow: 0 0 0 3px rgba(8, 70, 111, 0.15);
}

.form-group input::placeholder {
  color: var(--ns-gray-400);
}

.form-group input.field-error,
.form-group select.field-error {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* Select Wrapper */
.select-wrapper {
  position: relative;
}

.select-wrapper select {
  width: 100%;
  appearance: none;
  padding-right: 40px;
  cursor: pointer;
}

.select-arrow {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: var(--ns-gray-400);
  pointer-events: none;
}

/* Custom Dropdown */
/* State autocomplete */
.state-autocomplete {
  position: relative;
  z-index: 1;
}

.state-autocomplete:focus-within {
  z-index: 99999;
}

.state-input {
  width: 100%;
  padding: 14px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  font-size: 15px;
  color: var(--ns-gray-900);
  background: white;
  transition: all 0.2s;
  box-sizing: border-box;
}

.state-input:focus {
  border-color: #08466f;
  box-shadow: 0 0 0 3px rgba(8, 70, 111, 0.15);
  outline: none;
}

.state-autocomplete.field-error .state-input {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.state-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #08466f;
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 99999;
  max-height: 250px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.state-suggestion-item {
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: white;
  font-size: 15px;
  color: var(--ns-gray-900);
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.state-suggestion-item:hover,
.state-suggestion-item.is-highlighted {
  background: var(--ns-gray-50, #f9fafb);
}

.state-suggestion-item.is-selected {
  background: var(--ns-soft-blue, #c9e8ff);
  font-weight: 500;
}

.custom-dropdown {
  position: relative;
  z-index: 1;
}

.custom-dropdown.is-open {
  z-index: 9999;
}

.custom-dropdown .dropdown-trigger {
  width: 100%;
  padding: 14px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  font-size: 15px;
  color: var(--ns-gray-900);
  background: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;
}

.custom-dropdown .dropdown-trigger:focus {
  border-color: #08466f;
  box-shadow: 0 0 0 3px rgba(8, 70, 111, 0.15);
  outline: none;
}

.custom-dropdown.is-open .dropdown-trigger {
  border-color: #08466f;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.custom-dropdown.field-error .dropdown-trigger {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.custom-dropdown .dropdown-arrow {
  width: 20px;
  height: 20px;
  color: var(--ns-gray-400);
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.custom-dropdown.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  background: transparent;
  cursor: default;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #08466f;
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdown-menu-scrollable {
  max-height: 250px;
}

.dropdown-option {
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: white;
  font-size: 15px;
  color: var(--ns-gray-900);
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.15s;
}

.dropdown-option:hover {
  background: var(--ns-gray-50, #f9fafb);
}

.dropdown-option.is-selected {
  background: var(--ns-soft-blue, #c9e8ff);
  font-weight: 500;
}

/* DOB Row */
.dob-row {
  display: flex;
  gap: 12px;
  width: 100%;
}

.dob-row input {
  flex: 1;
  min-width: 0;
  text-align: center;
}

/* Form Row */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}


/* BMI Display */
.bmi-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--ns-soft-blue);
  border-radius: 8px;
  font-size: 14px;
  color: var(--ns-teal);
  animation: slideUp 0.4s ease-out both;
}

.bmi-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* Error Message */
.error-message {
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.continue-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px;
  background: linear-gradient(135deg, var(--ns-lime) 0%, #d4f072 100%);
  color: var(--ns-teal);
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.continue-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(233, 255, 134, 0.4);
}

.continue-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px;
  background: white;
  color: var(--ns-gray-600);
  border: 1px solid var(--ns-gray-300);
  border-radius: 50px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.back-btn:hover {
  background: var(--ns-gray-50);
  border-color: var(--ns-gray-400);
}

.btn-arrow {
  font-weight: 700;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--ns-teal);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Trust Row */
.trust-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 24px;
  border-top: 1px solid var(--ns-gray-100);
}

.trust-text {
  font-size: 12px;
  color: var(--ns-gray-400);
}

.trust-logo {
  height: 20px;
  width: auto;
}

/* SMS phone disclaimer (marketing — implied consent) */
.sms-phone-disclaimer {
    font-size: 11px;
    line-height: 1.4;
    color: #6b7280;
    margin: -8px 0 16px;
}
.sms-phone-disclaimer-link {
    color: inherit;
    text-decoration: underline;
}
@media screen and (max-width: 768px) {
    .sms-phone-disclaimer {
        font-size: 10px;
    }
}

/* SMS consent checkbox (beside disclaimer, above Find Your Treatment button) */
.sms-consent-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 1px 0 0;
    padding: 8px 10px;
    border: 1px solid transparent;
    border-radius: 9px;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}
.sms-consent-row .sms-phone-disclaimer {
    margin: 0;
    cursor: pointer;
    text-align: left;
}
.sms-consent-checkbox {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin: 2px 0 0;
    accent-color: #205089;
    cursor: pointer;
}
.sms-consent-row-error {
    border-color: #dc3545;
    background-color: rgba(220, 53, 69, 0.05);
}
.form-actions.sms-actions {
    position: relative;
}
.sms-consent-error {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    margin: 0 0 4px;
    color: #dc3545;
    font-size: 12px;
    line-height: 1.4;
    text-align: center;
}
.sms-consent-shake {
    animation: smsConsentShake 0.6s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}
.sms-consent-shake .sms-consent-checkbox {
    animation: smsConsentPulse 0.6s ease both;
}
@keyframes smsConsentShake {
    10%, 90% { transform: translateX(-1px); }
    20%, 80% { transform: translateX(2px); }
    30%, 50%, 70% { transform: translateX(-4px); }
    40%, 60% { transform: translateX(4px); }
}
@keyframes smsConsentPulse {
    0%, 100% { transform: scale(1); }
    30% { transform: scale(1.3); }
    60% { transform: scale(0.95); }
}

/* Checkbox */
.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  margin-top: 10px;
  justify-content: center;
}

@media (max-width: 767px) {
  .checkbox-label {
    justify-content: flex-start;
  }
}

.checkbox-input {
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  accent-color: var(--ns-teal);
  cursor: pointer;
}

.checkbox-text {
  font-size: 12px;
  color: var(--ns-gray-700);
  line-height: 1.4;
}

/* Consent Text */
.consent-text {
  font-size: 11px;
  color: var(--ns-gray-400);
  line-height: 1.6;
  margin: 0px 0 0 0;
}

/* Responsive */
@media (max-width: 640px) {
  .header-content {
    padding: 12px 16px;
  }

  .header-right {
    gap: 12px;
  }

  .phone-text {
    display: none;
  }

  .phone-icon-mobile {
    display: block;
  }

  .progress-bar {
    padding: 16px 12px;
  }

  .step-circle {
    width: 22px;
    height: 22px;
  }

  .check-icon {
    width: 12px;
    height: 12px;
  }

  .step-dot {
    width: 8px;
    height: 8px;
  }

  .step-line {
    width: 12px;
  }

  .header-back-btn {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .wizard-main {
    padding: 24px 16px;
  }

  .step-title {
    font-size: 24px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .dob-row {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .back-btn {
    order: 1;
  }

  .doctor-bar {
    flex-direction: column;
    text-align: center;
  }
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 400px;
  width: 100%;
  overflow: hidden;
  animation: modalSlideUp 0.3s ease-out;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-body {
  padding: 24px;
  text-align: center;
}

.modal-text {
  font-size: 16px;
  color: var(--ns-gray-700);
  line-height: 1.5;
  margin: 0 0 24px 0;
}

.modal-text strong {
  color: var(--ns-teal);
}

.modal-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 32px;
  background: linear-gradient(135deg, var(--ns-lime) 0%, #d4f072 100%);
  color: var(--ns-teal);
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.modal-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(233, 255, 134, 0.4);
}

/* Step 7: Treatment Cards */
.treatment-cards .treatment-card:nth-child(1) {
  animation: slideUp 0.4s ease-out 0.1s both;
}

.treatment-cards .treatment-card:nth-child(2) {
  animation: slideUp 0.4s ease-out 0.25s both;
}

.treatment-cards .treatment-card:nth-child(3) {
  animation: slideUp 0.4s ease-out 0.4s both;
}

.treatment-cards .treatment-card:nth-child(4) {
  animation: slideUp 0.4s ease-out 0.55s both;
}

.treatment-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 700px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .treatment-cards {
    grid-template-columns: 1fr;
    max-width: 500px;
  }
}

.treatment-card {
  background: white;
  border: 2px solid var(--ns-gray-200);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.treatment-card .klarna-badge {
  position: absolute;
  bottom: 4px;
  right: 8px;
  width: 50px;
  height: auto;
  border-radius: 4px;
  z-index: 2;
}

.treatment-card:hover {
  border-color: var(--ns-teal);
  box-shadow: 0 4px 12px rgba(8, 70, 111, 0.1);
}

.treatment-image {
  position: relative;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.treatment-image.tirz {
  background: linear-gradient(135deg, #e8f4fc 0%, #d4eaf7 100%);
}

.treatment-image.sema {
  background: linear-gradient(135deg, #fef9e8 0%, #fdf3d7 100%);
}

.treatment-image.mounjaro {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
}

.treatment-image.ozempic {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
}

.brand-card {
  opacity: 0.9;
}

.brand-card:hover {
  opacity: 1;
}

.price-tag.brand {
  background: var(--ns-gray-100);
  color: var(--ns-teal);
}

.price-tag.brand .price-amount {
  color: var(--ns-teal);
}

.trademark {
  font-size: 12px;
  vertical-align: super;
}

.treatment-image img {
  max-height: 120px;
  width: auto;
  object-fit: contain;
}

.price-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  background: var(--ns-teal);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.price-from {
  font-size: 10px;
  opacity: 0.8;
}

.price-amount {
  font-size: 18px;
  font-weight: 700;
}

.price-amount sup {
  font-size: 12px;
}

.treatment-info {
  padding: 16px;
}

.treatment-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.treatment-name-group {
  display: flex;
  flex-direction: column;
}

.treatment-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--ns-teal);
  margin: 0;
}

.treatment-name .plus {
  color: var(--ns-lime);
}

.treatment-sub {
  font-size: 11px;
  color: var(--ns-gray-500);
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.select-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, var(--ns-lime) 0%, #d4f072 100%);
  color: var(--ns-teal);
  border: none;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.select-btn:hover {
  transform: translateY(-1px);
}

.select-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--ns-teal);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

.treatment-desc {
  font-size: 14px;
  color: var(--ns-gray-600);
  margin: 0;
  line-height: 1.4;
}

/* Step 8: Inclusions */
.inclusions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.inclusions-list .inclusion-item:nth-child(1) {
  animation: slideUp 0.4s ease-out 0.1s both;
}

.inclusions-list .inclusion-item:nth-child(2) {
  animation: slideUp 0.4s ease-out 0.2s both;
}

.inclusions-list .inclusion-item:nth-child(3) {
  animation: slideUp 0.4s ease-out 0.3s both;
}

.inclusions-list .inclusion-item:nth-child(4) {
  animation: slideUp 0.4s ease-out 0.4s both;
}

.inclusions-list .inclusion-item:nth-child(5) {
  animation: slideUp 0.4s ease-out 0.5s both;
}

.inclusions-list .inclusion-item:nth-child(6) {
  animation: slideUp 0.4s ease-out 0.6s both;
}

.inclusions-step .pricing-summary-card {
  animation: slideUp 0.4s ease-out 0.7s both;
}

.inclusions-step .form-actions {
  animation: slideUp 0.4s ease-out 0.8s both;
}

.inclusions-step .trust-row {
  animation: slideUp 0.4s ease-out 0.9s both;
}

.inclusion-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  padding-right: 90px;
  background: white;
  border: 1px solid var(--ns-gray-200);
  border-radius: 12px;
}

.inclusion-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.inclusion-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
  border-radius: 8px;
  padding: 6px;
  overflow: hidden;
}

.inclusion-icon-bottom {
  align-items: flex-end;
  padding-bottom: 0;
}

.inclusion-icon-bottom img {
  max-width: 115%;
  max-height: 115%;
}

.inclusion-icon img {
  max-width: 110%;
  max-height: 110%;
  object-fit: contain;
}

.inclusion-text {
  flex: 1;
  min-width: 0;
}

.inclusion-text h4 {
  font-size: 14px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0 0 2px 0;
}

.inclusion-text p {
  font-size: 12px;
  color: var(--ns-gray-500);
  margin: 0;
  line-height: 1.4;
}

.inclusion-right {
  position: absolute;
  top: 12px;
  right: 12px;
}

.inclusion-price {
  font-size: 13px;
  color: var(--ns-gray-700);
  text-align: right;
}

.inclusion-price strong {
  font-size: 17px;
  color: var(--ns-teal);
}

.included-badge {
  display: inline-block;
  padding: 3px 10px;
  background: var(--ns-soft-blue);
  color: var(--ns-teal);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

/* Premium Pricing Summary Card */
.pricing-summary-card {
  position: relative;
  margin-top: 16px;
  padding: 3px;
  background: linear-gradient(135deg, var(--ns-lime) 0%, #b8e986 50%, var(--ns-lime) 100%);
  border-radius: 16px;
  box-shadow:
    0 4px 20px rgba(233, 255, 134, 0.3),
    0 2px 8px rgba(8, 70, 111, 0.1);
}

.pricing-card-inner {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 14px;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.pricing-card-inner::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle at top right, rgba(233, 255, 134, 0.2) 0%, transparent 70%);
  pointer-events: none;
}

.pricing-badge-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.pricing-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(135deg, var(--ns-teal) 0%, #0a5a8f 100%);
  color: var(--ns-lime);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(8, 70, 111, 0.3);
  animation: subtle-pulse 2s ease-in-out infinite;
}

.pricing-badge svg {
  flex-shrink: 0;
}

@keyframes subtle-pulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(8, 70, 111, 0.3);
  }
  50% {
    box-shadow: 0 4px 16px rgba(8, 70, 111, 0.4), 0 0 0 4px rgba(233, 255, 134, 0.15);
  }
}

.pricing-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.pricing-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pricing-label {
  font-size: 20px;
  font-weight: 700;
  color: var(--ns-teal);
  line-height: 1.2;
}

.pricing-subtitle {
  font-size: 13px;
  color: var(--ns-gray-500);
  font-weight: 500;
}

.pricing-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.pricing-comparison {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pricing-original {
  font-size: 14px;
  color: var(--ns-gray-400);
  text-decoration: line-through;
  font-weight: 500;
}

.pricing-savings {
  display: inline-block;
  padding: 2px 8px;
  background: #dcfce7;
  color: #15803d;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}

.pricing-final {
  display: flex;
  align-items: baseline;
  line-height: 1;
}

.pricing-currency {
  font-size: 22px;
  font-weight: 700;
  color: var(--ns-teal);
  margin-right: 2px;
}

.pricing-amount {
  font-size: 42px;
  font-weight: 800;
  color: var(--ns-teal);
  letter-spacing: -1px;
}

.pricing-period {
  display: inline;
  margin-left: 1px;
}

.pricing-period sup {
  font-size: 18px;
  font-weight: 700;
  color: var(--ns-teal);
}

.pricing-period span {
  font-size: 18px;
  font-weight: 500;
  color: var(--ns-gray-500);
}

.trust-row.trustpilot {
  flex-direction: column;
  gap: 8px;
  text-align: center;
}

.stars {
  color: #00b67a;
  font-size: 20px;
  letter-spacing: 2px;
}

.trust-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trust-rating {
  font-size: 14px;
  font-weight: 600;
  color: var(--ns-gray-700);
}

.trust-count {
  font-size: 12px;
  color: var(--ns-gray-400);
}

/* Step 9: Payment Plans */
.order-product {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  background: white;
  border: 1px solid var(--ns-gray-200);
  border-radius: 12px;
  margin-bottom: 20px;
  animation: slideUp 0.4s ease-out 0.1s both;
}

.product-info {
  flex: 1;
}

.product-name {
  font-size: 22px;
  font-weight: 700;
  color: var(--ns-teal);
  margin: 0 0 2px 0;
}

.product-name .plus {
  color: var(--ns-lime);
}

.product-sub {
  font-size: 11px;
  color: var(--ns-gray-500);
  letter-spacing: 0.5px;
}

.product-note {
  font-size: 12px;
  color: var(--ns-gray-400);
  margin: 8px 0 0 0;
}

.product-image {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.payment-plans {
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideUp 0.4s ease-out 0.15s both;
}

.plan-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border: 2px solid var(--ns-gray-200);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.plan-option:hover {
  border-color: var(--ns-gray-300);
}

.plan-option.selected {
  border-color: var(--ns-teal);
  background: rgba(201, 232, 255, 0.2);
}

.glp1-radio-circle {
  width: 22px;
  height: 22px;
  border: 2px solid var(--ns-gray-300);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}

.glp1-radio-circle.checked {
  border-color: var(--ns-teal);
}

.plan-details {
  flex: 1;
  min-width: 0;
}

.plan-price {
  font-size: 16px;
  color: var(--ns-gray-700);
  display: block;
}

.plan-price strong {
  font-size: 22px;
  color: var(--ns-teal);
}

.plan-note {
  font-size: 11px;
  color: var(--ns-gray-400);
  display: block;
  margin-top: 2px;
}

.plan-badge {
  padding: 6px 12px;
  background: var(--ns-gray-100);
  color: var(--ns-gray-600);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}

.plan-option.selected .plan-badge {
  background: var(--ns-lime);
  color: var(--ns-teal);
}

.plans-divider {
  font-size: 13px;
  color: var(--ns-gray-500);
  text-align: center;
  margin: 4px 0;
}

/* Mobile responsiveness for new steps */
@media (max-width: 640px) {
  .treatment-image {
    height: 140px;
  }

  .treatment-image img {
    max-height: 100px;
  }

  .inclusion-item {
    padding-right: 80px;
  }

  /* Pricing Summary Card - Mobile */
  .pricing-summary-card {
    margin-top: 12px;
  }

  .pricing-card-inner {
    padding: 16px;
  }

  .pricing-badge-wrapper {
    margin-bottom: 12px;
  }

  .pricing-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .pricing-left {
    align-items: center;
  }

  .pricing-right {
    align-items: center;
  }

  .pricing-amount {
    font-size: 36px;
  }

  .pricing-currency {
    font-size: 18px;
  }

  .order-product {
    flex-direction: column;
    text-align: center;
  }

  .product-image {
    order: -1;
  }

  .plan-option {
    flex-wrap: wrap;
  }

  .plan-badge {
    width: 100%;
    text-align: center;
    margin-top: 8px;
  }
}

/* 2-Step Checkout Stepper */
.checkout-stepper .progress-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
}

.checkout-step {
  display: flex;
  align-items: center;
  gap: 12px;
}

.checkout-step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid var(--ns-teal);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.checkout-step-circle.active {
  border-color: var(--ns-teal);
  background: var(--ns-teal);
}

.checkout-step-circle.active .step-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
}

.checkout-step-circle .step-dot-outline {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid var(--ns-teal);
  background: transparent;
}

.checkout-step-label {
  font-size: 15px;
  font-weight: 500;
  color: var(--ns-gray-500);
}

.checkout-step.active .checkout-step-label {
  color: var(--ns-teal);
  font-weight: 600;
}

.checkout-step-line {
  width: 80px;
  height: 2px;
  background: var(--ns-gray-300);
  margin: 0 16px;
}

/* Step 9 Product Selection */
.select-product-step {
  max-width: 800px;
}

.product-selection-cards {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;
}

.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 2px solid var(--ns-gray-200);
  cursor: pointer;
  transition: all 0.2s ease;
}

.product-card:hover {
  border-color: var(--ns-teal);
  box-shadow: 0 4px 16px rgba(8, 70, 111, 0.12);
}

.product-card-image {
  position: relative;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-card-image.tirz {
  background: linear-gradient(135deg, #e8f4f8 0%, #d1e8f0 100%);
}

.product-card-image.sema {
  background: linear-gradient(135deg, #f0f4e8 0%, #e4ecd1 100%);
}

.product-card-image img {
  max-width: 120px;
  height: auto;
}

.product-card-image .price-tag {
  position: absolute;
  top: 16px;
  right: 16px;
  background: var(--ns-teal);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.product-card-image .price-from {
  font-size: 11px;
  opacity: 0.8;
}

.product-card-image .price-amount {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.product-card-image .price-amount sup {
  font-size: 12px;
  vertical-align: super;
}

.product-card-image .price-period {
  font-size: 12px;
  opacity: 0.8;
}

.product-card-info {
  padding: 20px;
}

.product-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.product-name-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-card-name {
  font-size: 22px;
  font-weight: 700;
  color: var(--ns-teal);
  margin: 0;
}

.product-card-name .plus {
  color: var(--ns-green);
}

.product-card-sub {
  font-size: 11px;
  font-weight: 600;
  color: var(--ns-gray-500);
  letter-spacing: 0.5px;
}

.select-product-btn {
  background: linear-gradient(135deg, var(--ns-lime) 0%, #d4f072 100%);
  color: var(--ns-teal);
  border: none;
  border-radius: 50px;
  padding: 12px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
}

.select-product-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(233, 255, 134, 0.4);
}

.select-product-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.product-card-desc {
  font-size: 14px;
  color: var(--ns-gray-600);
  line-height: 1.5;
  margin: 0;
}

/* Mobile adjustments for Step 9 */
@media (max-width: 480px) {
  .checkout-step-line {
    width: 40px;
    margin: 0 8px;
  }

  .checkout-step-label {
    font-size: 13px;
  }

  .checkout-step-circle {
    width: 28px;
    height: 28px;
  }

  .product-card-image img {
    max-width: 100px;
  }

  .product-card-image .price-amount {
    font-size: 20px;
  }

  .product-card-header {
    flex-direction: column;
    gap: 12px;
  }

  .select-product-btn {
    width: 100%;
  }
}

/* Step 9: Choose Plan Styles - Horizontal Rows */
.choose-plan-step {
  max-width: 700px;
  margin: 0 auto;
}

.choose-plan-step .plan-row:nth-child(1) {
  animation: slideUp 0.4s ease-out 0.1s both;
}

.choose-plan-step .plan-row:nth-child(2) {
  animation: slideUp 0.4s ease-out 0.2s both;
}

.choose-plan-step .plan-row:nth-child(3) {
  animation: slideUp 0.4s ease-out 0.3s both;
}

.choose-plan-step .plan-row:nth-child(4) {
  animation: slideUp 0.4s ease-out 0.4s both;
}

.choose-plan-step .form-actions {
  animation: slideUp 0.4s ease-out 0.5s both;
}

.plan-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Step 9 Layout - Exact sizing from reference */
.step-9-layout {
  font-family: var(--satoshi), sans-serif;
  max-width: 896px;
  margin: 0 auto;
}

.step-9-layout .step-header {
  margin-bottom: 16px;
}

.step-9-layout .step-title {
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
}

/* Step 9 staggered animations */
.step-9-layout .step-header {
  animation: slideUp 0.4s ease-out both;
}

.step-9-layout .product-info-card {
  animation: slideUp 0.4s ease-out 0.1s both;
}

.step-9-layout .plan-row:nth-child(1) {
  animation: slideUp 0.4s ease-out 0.2s both;
}

.step-9-layout .commitment-heading {
  animation: slideUp 0.4s ease-out 0.3s both;
}

.step-9-layout .plan-row:nth-child(3) {
  animation: slideUp 0.4s ease-out 0.35s both;
}

.step-9-layout .plan-row:nth-child(4) {
  animation: slideUp 0.4s ease-out 0.4s both;
}

.step-9-layout .plan-row:nth-child(5) {
  animation: slideUp 0.4s ease-out 0.45s both;
}

.step-9-layout .form-actions {
  animation: slideUp 0.4s ease-out 0.5s both;
}

.step-9-layout .order-summary-card {
  animation: slideUp 0.4s ease-out 0.2s both;
}

.step-9-grid {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
}

.step-9-left {
  width: 69%;
  background: #f6f6f6;
  border: 2px solid #dfdfdf;
  border-radius: 12px;
  padding: 20px;
}

.step-9-right {
  width: 29.5%;
  flex-shrink: 0;
}

/* Product Info Card - exact sizing from reference */
.product-info-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border: 2px solid #dfdfdf;
  border-radius: 12px;
  overflow: hidden;
  height: 102px;
  margin-bottom: 16px;
}

.product-info-content {
  height: 100px;
  padding: 10px 20px 20px 20px;
  box-sizing: border-box;
}

.product-info-header {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 10px;
  height: 44px;
  width: 402px;
  margin-bottom: 6px;
}

.product-name-section {
  height: 38px;
  width: 128px;
  flex-shrink: 0;
}

.product-name {
  font-size: 20px;
  font-weight: 900;
  color: #1f2937;
  line-height: 24px;
  height: 24px;
  width: 128px;
}

.product-plus {
  color: #0c9ac4;
}

.product-compound {
  font-size: 8px;
  font-weight: 900;
  color: #80888d;
  text-transform: uppercase;
  line-height: 12px;
  white-space: nowrap;
}

.dosage-boxes {
  display: flex;
  gap: 6px;
  height: 44px;
  width: 198px;
  margin-left: 12px;
}

.dosage-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: rgb(243, 243, 243);
  border-radius: 4px;
  padding: 2px 6px;
  height: 44px;
  width: 62px;
  border: none !important;
  overflow: hidden;
}

.dosage-box:first-child {
  width: 66px;
}

.dosage-month {
  font-size: 8px;
  color: #1f2937;
  text-align: center;
  line-height: 12px;
  height: 12px;
  white-space: nowrap;
}

.dosage-amount {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  line-height: 14px;
  height: 14px;
  white-space: nowrap;
}

.dosage-unit {
  font-size: 10px;
  line-height: 10px;
}

.dosage-freq {
  font-size: 8px;
  color: #1f2937;
  text-align: center;
  line-height: 12px;
  height: 12px;
  white-space: nowrap;
}

.product-info-note {
  font-size: 14px;
  color: #1f2937;
  line-height: 20px;
  height: 20px;
  width: 402px;
}

.product-info-image {
  width: 100px;
  height: 100px;
  background: var(--ns-soft-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.product-info-image img {
  width: 100px;
  height: 100px;
  max-width: 100%;
  object-fit: contain;
}

/* Plan Rows - 56px height */
.plan-row {
  background: white;
  border: 2px solid #dfdfdf;
  border-radius: 10px;
  padding: 16px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  position: relative;
  height: 56px;
  box-sizing: border-box;
}

.plan-row:hover {
  border-color: var(--ns-soft-blue);
}

.plan-row.selected {
  background: var(--ns-soft-blue);
  border-color: #dfdfdf;
}

/* More space between plan rows so the badges don't overlap the card above */
.plan-row {
  margin-bottom: 16px;
}

/* 12-month card grows to fit the HUGE SAVINGS button */
.plan-row--with-savings {
  height: auto;
  min-height: 72px;
  padding-top: 12px;
  padding-bottom: 12px;
}

.plan-row--with-savings .plan-row-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

/* Plan badges (Popular / Recommended) */
.plan-badge {
  position: absolute;
  top: -13px;
  left: 18px;
  z-index: 2;
  background-color: #d7fff0;
  color: #000;
  font-family: var(--satoshi, 'Satoshi', sans-serif);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 6px 11px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}


.plan-row-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.glp1-radio-circle {
  width: 20px;
  height: 20px;
  border: 2px solid #dfdfdf;
  border-radius: 50%;
  background: white;
  flex-shrink: 0;
}

.glp1-radio-circle.checked {
  border-color: #0c9ac4;
  background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Ccircle cx='10' cy='10' r='5' fill='%230c9ac4'/%3E%3C/svg%3E") center/contain no-repeat;
}

.plan-row-info {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.plan-row-price-inline {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  line-height: 24px;
}

.plan-row-price-inline strong {
  font-size: 14px;
}

.plan-row-price-inline sup {
  font-size: 12px;
  position: relative;
}

.plan-duration {
  font-weight: 400;
  font-size: 14px;
}

.plan-row-recurring {
  font-size: 14px;
  color: #1f2937;
}

.plan-row-today {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.plan-disclaimer {
  font-size: 8px;
  color: #808080;
  position: absolute;
  top: 34px;
  left: 40px;
}

.commitment-heading {
  font-size: 14px;
  font-weight: 400;
  color: #1f2937;
  margin: 10px 0 10px 10px;
}

.plan-row-right {
  text-align: right;
  flex-shrink: 0;
  padding-right: 10px;
}

/* Order Summary - exact sizing from reference */
.order-summary-card {
  background: #f6f6f6;
  border: 2px solid #dfdfdf;
  border-radius: 12px;
  padding: 20px;
  font-family: var(--satoshi), sans-serif;
}

.order-summary-header {
  font-size: 18px;
  font-weight: 400;
  color: #1f2937;
  padding-bottom: 20px;
  border-bottom: 2px solid #dfdfdf;
  line-height: 20px;
}

.order-summary-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 0;
  border-bottom: 2px solid #dfdfdf;
  font-size: 14px;
  line-height: 16px;
}

.order-summary-line span:first-child {
  width: 70%;
}

.order-summary-price {
  font-size: 14px;
  font-weight: 700;
  text-align: right;
  line-height: 18px;
}

.order-summary-price sup {
  font-size: 10px;
  position: relative;
}

.order-summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
}

.order-summary-due {
  font-weight: 900;
  font-size: 14px;
  line-height: 20px;
}

.order-summary-due-price {
  font-size: 18px;
  font-weight: 700;
  text-align: right;
  line-height: 18px;
}

.order-summary-due-price sup {
  font-size: 12px;
  position: relative;
}

.order-summary-guarantee {
  font-size: 16px;
  line-height: 1.4;
  color: #1f2937;
  margin-top: 6px;
}

/* Dark blue continue button for Step 9 */
.step-9-left .form-actions {
  margin-top: 26px;
}

.step-9-left .continue-btn {
  width: 100%;
  height: 50px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 50px;
  padding: 14px 16px 16px;
}

.continue-btn-dark {
  background: #205089 !important;
  border: 2px solid #205089 !important;
  color: white !important;
}

.continue-btn-dark:hover {
  background: #1a4070 !important;
  border-color: #1a4070 !important;
}

.continue-btn-dark .btn-arrow {
  font-weight: 800;
}

/* Mobile responsive for Step 9 */
@media (max-width: 768px) {
  /* Checkout Stepper - more padding and spacing on mobile */
  .checkout-stepper {
    padding: 24px 16px;
    border-bottom: 2px solid var(--ns-gray-100);
  }

  .checkout-step-circle {
    width: 24px;
    height: 24px;
  }

  .checkout-step-circle.active .step-dot {
    width: 8px;
    height: 8px;
  }

  .checkout-step-label {
    font-size: 12px;
    font-weight: 600;
  }

  .checkout-step-line {
    width: 80px;
    margin: 0 16px;
  }

  .step-9-layout {
    padding: 0;
  }

  .step-9-grid {
    flex-direction: column;
    gap: 0;
  }

  .step-9-left {
    width: 100%;
    padding: 16px;
    border-radius: 12px;
  }

  /* Hide Order Summary on mobile */
  .step-9-right {
    display: none;
  }

  /* Product Info Card - horizontal layout on mobile with image on right */
  .product-info-card {
    flex-direction: row;
    height: auto;
    min-height: 148px;
  }

  .product-info-content {
    flex: 1;
    min-width: 0;
    height: auto;
    padding: 12px 12px 12px 16px;
  }

  .product-info-header {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    height: auto;
    gap: 8px;
  }

  .product-name-section {
    width: auto;
  }

  .product-name {
    font-size: 18px;
    width: auto;
  }

  .product-compound {
    font-size: 8px;
  }

  .dosage-boxes {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 4px;
  }

  .dosage-box {
    width: auto;
    min-width: 54px;
    padding: 2px 4px;
  }

  .dosage-box:first-child {
    width: auto;
    min-width: 56px;
  }

  .dosage-month {
    font-size: 9px;
  }

  .dosage-amount {
    font-size: 16px;
  }

  .dosage-unit {
    font-size: 9px;
  }

  .dosage-freq {
    font-size: 9px;
  }

  .product-info-note {
    width: 100%;
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.4;
  }

  .product-info-image {
    width: 74px;
    height: 148px;
    flex-shrink: 0;
  }

  .product-info-image img {
    width: 74px;
    height: 74px;
  }

  /* Plan Rows - proper mobile layout matching reference */
  .plan-row {
    height: 68px;
    padding: 14px 10px;
    position: relative;
    flex-direction: row;
    align-items: center;
  }

  .plan-row--with-savings {
    height: auto;
    min-height: 84px;
  }

  .plan-row--with-savings .plan-row-right {
    position: static;
    transform: none;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  .plan-row-left {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 65%;
  }

  .plan-row-info {
    flex: 1;
    min-width: 0;
  }

  .plan-row-price-inline {
    font-size: 18px;
    line-height: 24px;
  }

  .plan-row-price-inline strong {
    font-size: 18px;
  }

  .plan-duration {
    font-size: 12px;
    display: block;
  }

  .plan-row-right {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    padding-right: 0;
    text-align: right;
  }

  .plan-row-today {
    font-size: 13px;
    font-weight: 700;
  }

  .plan-row-recurring {
    font-size: 13px;
  }

  .plan-disclaimer {
    position: absolute;
    top: 42px;
    left: 40px;
    right: 10px;
    font-size: 7px;
    line-height: 1.3;
  }

  .commitment-heading {
    font-size: 14px;
    margin: 8px 0;
  }

  .step-9-left .continue-btn {
    height: 54px;
    font-size: 16px;
  }

  .step-9-left .form-actions {
    margin-top: 20px;
  }
}

/* Email disclaimer — sits tight under the input; error renders below it */
.paragraph-218 {
  margin-top: 3px;
  margin-bottom: 6px;
}

/* Email validation error — red, below the disclaimer */
.email-check-error {
  color: #dc3545;
  font-size: 13px;
  margin-top: 0;
}

/* SMS Terms & Privacy Policy link color */
::v-deep .paragraph-218 a {
  color: #08466f !important;
}
</style>
